import { ChangeEvent, FormEvent, ReactNode, useEffect, useRef, useState } from "react";
import { ListControls, usePagedList } from "./Pagination";
import { DeviceMosaic } from "./DeviceMosaic";
import {
  api,
  ActivationKey,
  ActivityEvent,
  ActivitySuggestion,
  ActivityUsageBucket,
  ActivityUsageReport,
  ActivityUsageTimeline,
  AuditEvent,
  CompanyAccess,
  Device,
  DeviceDetail,
  DeviceEvent,
  DeviceIncident,
  DeviceInventory,
  EventImport,
  EventReportSummary,
  ManagedUser,
  Partner,
  Plan,
  PlatformSettings,
  PlatformUser,
  Policy,
  session,
  SnapshotView,
  SupportGrant,
  NetworkStatus,
  SystemStatus,
  Tenant,
  TenantEvent,
  UserCompanyRole,
  TimelineGap,
  User,
} from "./api";

// Cabeçalho comum de página: título/subtítulo à esquerda, ações à direita.
// Padroniza a hierarquia (título → ação), antes duplicada a cada tela.
function PageHeader({ eyebrow, title, subtitle, actions }: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="page-heading">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {subtitle && <p className="muted">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

// Barra de filtros comum: rótulo "Filtros", campos e ações (Filtrar/Limpar) agrupados.
function FilterBar({ children, actions, onSubmit }: {
  children: ReactNode;
  actions?: ReactNode;
  onSubmit?: (event: FormEvent) => void;
}) {
  return (
    <form
      className="panel filter-bar"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit?.(event);
      }}
    >
      <div className="filter-bar-head">
        <span className="filter-bar-label">Filtros</span>
      </div>
      <div className="filter-bar-fields">{children}</div>
      {actions && <div className="filter-bar-actions">{actions}</div>}
    </form>
  );
}

const bytes = (value?: number) =>
  value == null
    ? "—"
    : value < 1024
      ? `${value} B`
      : value < 1024 ** 2
        ? `${(value / 1024).toFixed(1)} KB`
        : value < 1024 ** 3
          ? `${(value / 1024 ** 2).toFixed(1)} MB`
          : `${(value / 1024 ** 3).toFixed(1)} GB`;
const percent = (value?: number) =>
  value == null ? "—" : `${value.toFixed(1)}%`;
const duration = (value?: number) => {
  if (value == null) return "—";
  // Relatórios de um dia podem ter totais abaixo de 1 h: mostrar minutos evita "0d 0h".
  const total = Math.max(0, Math.round(value));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${total}s`;
};
const lifecycleLabel = (state: Device["lifecycleState"]) =>
  ({ ready: "Ativo", paused: "Pausado", revoked: "Revogado" })[state];
// O badge antigo só sabia se o processo existia. Com o diagnóstico da 0.16.0 dá para
// diferenciar sessão remota, política não lida, captura desligada e falha de envio.
function companionBadge(d: Device): { label: string; tone: "online" | "offline" | "warning"; title: string } | null {
  if (typeof d.sessionCompanionRunning !== "boolean") return null;
  if (!d.sessionCompanionRunning) {
    return {
      label: "Companion ausente",
      tone: "offline",
      title: "O serviço não encontrou o Guardian.Session em uma sessão interativa. Sem o companion não há captura de tela (o serviço roda na sessão 0).",
    };
  }
  const c = d.companion;
  if (!c) {
    return {
      label: "Companion ativo",
      tone: "online",
      title: "Agente sem diagnóstico detalhado (anterior à 0.16.0). Atualize o agente para ver política e último envio.",
    };
  }
  const details = [
    c.sessionId != null ? `sessão ${c.sessionId}` : null,
    c.policyRevision != null ? `política r${c.policyRevision}` : null,
    c.statusAt ? `status ${new Date(c.statusAt).toLocaleTimeString("pt-BR")}` : null,
    c.lastSendOkAt ? `último envio ${new Date(c.lastSendOkAt).toLocaleTimeString("pt-BR")}` : "nenhum envio registrado",
    c.lastError ? `erro: ${c.lastError}` : null,
  ].filter(Boolean).join(" · ");
  if (c.terminalServerSession)
    return { label: "Companion: sessão remota", tone: "warning", title: `Sessão RDP: a captura é ignorada por design. ${details}` };
  // statusAt vem do relógio da máquina do agente. Corrige pelo drift medido no heartbeat
  // (servidor - agente) para não acusar "sem sinal" em PC com relógio atrasado/adiantado.
  const driftMs = (d.clockDriftSeconds ?? 0) * 1000;
  const statusAge = c.statusAt ? Date.now() - new Date(c.statusAt).getTime() - driftMs : Number.POSITIVE_INFINITY;
  if (statusAge > 120_000)
    return { label: "Companion sem sinal", tone: "warning", title: `Sem status recente do companion. ${details}` };
  if (!c.policyLoaded)
    return { label: "Companion sem política", tone: "warning", title: `O companion não conseguiu ler o policy.json. ${details}` };
  if (!c.screenshotsEnabled)
    return { label: "Companion: captura desligada", tone: "warning", title: `A política aplicada está com a captura desabilitada. ${details}` };
  if (c.lastError)
    return { label: "Companion com erro", tone: "warning", title: `Última falha: ${c.lastError}. ${details}` };
  return { label: "Companion ativo", tone: "online", title: details };
}
// O token trocado para entrar numa empresa recebe a claim `scoped_tenant` e perde
// acesso a /platform. Usamos a claim (com fallback para o resumo salvo pela API) em
// vez do papel, que não distingue o Suporte da plataforma do Suporte em escopo.
// A API emite as capacidades no login (e em /auth/me). Menu e botões leem daqui em
// vez de comparar papel: papel não distingue, por exemplo, Suporte da plataforma de
// Suporte em escopo.
function can(user: User | null | undefined, capability: string) {
  return user?.capabilities?.includes(capability) ?? false;
}

function isScopedSession(token: string | null | undefined, user: User | null | undefined) {
  if (user?.scopedTenantId) return true;
  if (!token) return false;
  try {
    const payload = token.split(".")[1].replaceAll("-", "+").replaceAll("_", "/");
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { scoped_tenant?: string };
    return Boolean(claims.scoped_tenant);
  } catch {
    return false;
  }
}

const roleLabel = (role?: string) =>
  ({
    owner: "ADM da empresa",
    admin: "Administrador",
    support: "Suporte",
    viewer: "User",
    platform_admin: "Master",
    platform_support: "Suporte da plataforma",
    partner_admin: "Revenda — ADM",
    partner_support: "Revenda — Suporte",
  })[role ?? ""] ?? "";
const reasonLabel = (reason?: string) =>
  ({
    periodic: "Periódica",
    visual_change: "Mudança visual",
    mouse_click: "Clique",
    enter_key: "Enter",
    window_changed: "Troca de janela",
  })[reason ?? ""] ?? "Captura";
const localInput = (date: Date) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
type Theme = "dark" | "light";
const readTheme = (): Theme => localStorage.getItem("dbguardian.theme") === "light" ? "light" : "dark";
const applyTheme = (theme: Theme) => { document.documentElement.dataset.theme = theme; localStorage.setItem("dbguardian.theme", theme); };

export function App() {
  const [route, setRoute] = useState(
    window.location.hash || (session.token() ? "#/" : "#/login"),
  );
  // Sessões abertas antes da 0.29.0 não têm capacidades: o token também não tem a claim
  // "cap", então além do resumo é preciso reemitir o token (refresh) — só reidratar o
  // usuário deixaria o menu certo e as chamadas à API sem permissão.
  const [hydrated, setHydrated] = useState(() => !session.token() || Boolean(session.user()?.capabilities));
  useEffect(() => {
    const token = session.token();
    if (hydrated || !token) return;
    api
      .refresh()
      .then((result) => session.save(result.accessToken, result.user))
      .catch(() => session.clear())
      .finally(() => setHydrated(true));
  }, [hydrated]);
  useEffect(() => {
    applyTheme(readTheme());
    const change = () => setRoute(window.location.hash || "#/");
    addEventListener("hashchange", change);
    return () => removeEventListener("hashchange", change);
  }, []);
  if (!session.token() || route === "#/login")
    return <Login onSignedIn={() => setRoute("#/")} />;
  // Perfis privilegiados sem segundo fator recebem um token limitado: só dá para
  // cadastrar o MFA (e chamar /auth/me) até confirmar o código.
  if (session.user()?.mfaEnrollmentRequired) return <MfaEnrollment />;
  if (!hydrated) return <p className="muted">Carregando…</p>;
  const deviceId = route.match(/^#\/devices\/([a-f0-9-]+)$/i)?.[1];
  const timelineId = route.match(/^#\/devices\/([a-f0-9-]+)\/timeline(?:\?.*)?$/i)?.[1];
  const page = timelineId ? (
    <TimelinePage deviceId={timelineId} />
  ) : deviceId ? (
    <DevicePage id={deviceId} />
  ) : route === "#/users" ? (
    <UsersPage />
  ) : route === "#/audit" ? (
    <AuditPage />
  ) : route === "#/events" ? (
    <EventsPage />
  ) : route === "#/reports" ? (
    <ReportsPage />
  ) : route === "#/policies" ? (
    <PoliciesPage />
  ) : route === "#/security" ? (
    <SecurityPage />
  ) : route === "#/companies" ? (
    <CompaniesPage />
  ) : route === "#/plans" ? (
    <PlansPage />
  ) : route === "#/settings" ? (
    <SettingsPage />
  ) : route === "#/activity" ? (
    <ActivityPage />
  ) : (
    <Dashboard />
  );
  return <Shell route={route}>{page}</Shell>;
}

function NavIcon({ name }: { name: string }) {
  const paths: Record<string, ReactNode> = {
    devices: <><rect x="2.5" y="4" width="19" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></>,
    activity: <path d="M3 12h4l2.5-6 3.5 12 2.5-6H21" />,
    events: <><path d="M12 4 2.7 20h18.6L12 4Z" /><path d="M12 10v4.6M12 17.3v.1" /></>,
    reports: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
    policies: <><path d="M6 3h9l4 4v14H6z" /><path d="M15 3v5h4M9 12h7M9 16h7" /></>,
    users: <><circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 5.5a3.2 3.2 0 0 1 0 6M17 14.2c2.4.7 4 3 4 5.8" /></>,
    security: <><path d="M12 3l7 3v6c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-3Z" /><path d="M9.4 12l1.9 1.9L15 10" /></>,
    settings: <><path d="M4 7h8M17 7h3M4 17h3M11 17h9" /><circle cx="14.5" cy="7" r="2.2" /><circle cx="9" cy="17" r="2.2" /></>,
    audit: <><path d="M8 4h11v16H8z" /><path d="M8 4H5v16h3M11 9h5M11 13h5" /></>,
    companies: <><path d="M4 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16" /><path d="M14 9h5a1 1 0 0 1 1 1v11M2 21h20" /><path d="M7 8h3M7 12h3M7 16h3M17 13h2M17 17h2" /></>,
    plans: <><path d="M12 3 3 8l9 5 9-5-9-5Z" /><path d="M3 13l9 5 9-5M3 17.5l9 5 9-5" /></>,
  };
  return (
    <svg className="nav-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name] ?? paths.devices}
    </svg>
  );
}

function Shell({
  children,
  route,
}: {
  children: React.ReactNode;
  route: string;
}) {
  const user = session.user();
  const [theme, setTheme] = useState<Theme>(readTheme());
  const [widget, setWidget] = useState<{ supportWidgetEnabled: boolean; supportWidgetHtml: string }>();
  useEffect(() => { api.uiSettings().then(setWidget).catch(() => undefined); }, []);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("guardian.sidebar") === "collapsed");
  const toggleSidebar = () => setCollapsed((value) => {
    const next = !value;
    localStorage.setItem("guardian.sidebar", next ? "collapsed" : "expanded");
    return next;
  });
  const [mobileNav, setMobileNav] = useState(false);
  useEffect(() => { setMobileNav(false); }, [route]);
  // O Master entra em outra empresa com role "support" no token de escopo; a claim
  // platform_role é o que mantém o acesso ao conteúdo e à auditoria.
  // As telas de plataforma (/platform/*) exigem token sem escopo. O escopo não se
  // deduz do papel: o Suporte da plataforma tem role "support" na empresa de origem e
  // ainda precisa de "Empresas" para entrar nas empresas com concessão.
  const scopedSession = isScopedSession(session.token(), user);
  // Multiempresa: o seletor só aparece quando há mais de uma empresa disponível.
  const [companies, setCompanies] = useState<CompanyAccess[]>([]);
  useEffect(() => { api.companies().then((page) => setCompanies(page.items)).catch(() => undefined); }, []);
  const currentCompany = companies.find((company) => company.current);
  const [switchError, setSwitchError] = useState("");
  const switchCompany = async (tenantId: string) => {
    if (!tenantId || tenantId === currentCompany?.tenantId) return;
    setSwitchError("");
    try {
      const result = await api.enterCompany(tenantId);
      session.save(result.accessToken, result.user);
      // Recarregar garante que menu, capacidades e escopo venham do token novo.
      location.reload();
    } catch (error) {
      // Sem isso, um 403 (concessão revogada, usuário desativado) sumia em silêncio
      // e o seletor ficava exibindo a empresa nova sem token correspondente.
      setSwitchError(error instanceof Error ? error.message : "Não foi possível trocar de empresa.");
      void api.companies().then((page) => setCompanies(page.items)).catch(() => undefined);
    }
  };
  const navItems = [
    // Operação: o dia a dia de quem acompanha os equipamentos.
    {
      href: "#/",
      icon: "devices",
      label: "Equipamentos",
      group: "Operação",
      show: true,
    },
    {
      href: "#/activity",
      icon: "activity",
      label: "Atividade do usuário",
      group: "Operação",
      show: can(user, "content.read"),
    },
    {
      href: "#/events",
      icon: "events",
      label: "Eventos do Windows",
      group: "Operação",
      show: can(user, "content.read"),
    },
    // Análise: visões agregadas.
    { href: "#/reports", icon: "reports", label: "Relatórios", group: "Análise", show: can(user, "content.read") },
    // Administração da empresa.
    { href: "#/policies", icon: "policies", label: "Políticas", group: "Administração", show: true },
    { href: "#/users", icon: "users", label: "Usuários", group: "Administração", show: can(user, "user.manage") },
    { href: "#/security", icon: "security", label: "Segurança", group: "Administração", show: true },
    // Plataforma: só quem administra empresas/planos.
    {
      href: "#/companies",
      icon: "companies",
      // Revenda entra pela carteira; quem administra empresas vê "Empresas".
      label: can(user, "tenant.manage") ? "Empresas" : "Carteira",
      group: "Plataforma",
      show: (can(user, "tenant.manage") || can(user, "portfolio.read")) && !scopedSession,
    },
    { href: "#/plans", icon: "plans", label: "Planos e contratos", group: "Plataforma", show: can(user, "plan.manage") && !scopedSession },
    { href: "#/settings", icon: "settings", label: "Configurações", group: "Plataforma", show: true },
    // Governança.
    { href: "#/audit", icon: "audit", label: "Auditoria", group: "Governança", show: can(user, "audit.read") },
  ];
  const navSections = ["Operação", "Análise", "Administração", "Plataforma", "Governança"]
    .map((label) => ({ label, items: navItems.filter((item) => item.show && item.group === label) }))
    .filter((section) => section.items.length > 0);
  const isActive = (href: string) => route === href || (href === "#/" && route.startsWith("#/devices"));
  const toggleTheme = () => { const next = theme === "dark" ? "light" : "dark"; setTheme(next); applyTheme(next); };
  return (
    <div className={"app-shell" + (collapsed ? " sidebar-collapsed" : "")}>
      <div className="topbar">
        <button className="menu-button" onClick={() => setMobileNav(true)} aria-label="Abrir menu" aria-expanded={mobileNav} aria-controls="mobile-nav">☰</button>
        <span className="topbar-brand">Guardian</span>
        <div className="topbar-company">
          {companies.length > 1 ? (
            <select
              value={currentCompany?.tenantId ?? ""}
              onChange={(event) => void switchCompany(event.target.value)}
              aria-label="Empresa"
            >
              {companies.map((company) => (
                <option key={company.tenantId} value={company.tenantId}>
                  {company.tenantName || company.slug || "Empresa atual"}
                </option>
              ))}
            </select>
          ) : (
            <span className="topbar-company-name">
              {currentCompany?.tenantName || currentCompany?.slug || user?.displayName || ""}
            </span>
          )}
        </div>
        {switchError && <small className="company-switch-error" role="alert">{switchError}</small>}
        <span className="topbar-spacer" />
        <span className="topbar-user">{user?.displayName}</span>
        <button
          className="topbar-logout"
          onClick={() => {
            session.clear();
            location.hash = "#/login";
          }}
        >
          Sair
        </button>
      </div>
      <aside className={"sidebar" + (collapsed ? " collapsed" : "")}>
        <div className="brand-box">
          <img
            className="brand-logo"
            src="/brand/dbarros-horizontal.png"
            alt="Guardian Insight"
          />
          <strong>Guardian</strong>
          <span className="brand-mark" aria-hidden="true">G</span>
        </div>
        <nav>
          {navSections.map((section) => (
            <div className="nav-section" key={section.label}>
              <span className="nav-section-label">{section.label}</span>
              {section.items.map((item) => (
                <a key={item.href} href={item.href} className={isActive(item.href) ? "active" : ""} title={item.label}>
                  <span className="nav-icon"><NavIcon name={item.icon} /></span>
                  <span className="nav-label">{item.label}</span>
                </a>
              ))}
            </div>
          ))}
        </nav>
        <div className="account">
          {companies.length > 1 && (
            <select
              className="company-switcher"
              value={currentCompany?.tenantId ?? ""}
              onChange={(event) => void switchCompany(event.target.value)}
              title="Empresa"
              aria-label="Empresa"
            >
              {companies.map((company) => (
                <option key={company.tenantId} value={company.tenantId}>
                  {company.tenantName || company.slug || "Empresa atual"}
                </option>
              ))}
            </select>
          )}
          {switchError && <small className="company-switch-error" role="alert">{switchError}</small>}
          <span>{user?.displayName}</span>
          <small>{user?.platformRole === "platform_admin" ? "Master" : roleLabel(user?.role)}</small>
          {user?.platformRole && <small>Acesso Guardian Insight</small>}
        </div>
        <button
          className="sidebar-toggle"
          onClick={toggleSidebar}
          title={collapsed ? "Expandir menu" : "Recolher menu"}
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
        >
          <span className="nav-icon" aria-hidden="true">{collapsed ? "»" : "«"}</span>
          <span className="nav-label">{collapsed ? "Expandir" : "Recolher"}</span>
        </button>
        <button
          className="logout"
          onClick={() => {
            session.clear();
            location.hash = "#/login";
          }}
        >
          <span className="nav-icon" aria-hidden="true">⎋</span>
          <span className="nav-label">Sair</span>
        </button>
      </aside>
      {mobileNav && <div className="nav-backdrop" onClick={() => setMobileNav(false)} />}
      <nav id="mobile-nav" className={"mobile-nav" + (mobileNav ? " open" : "")} aria-hidden={!mobileNav}>
        <div className="mobile-nav-head">
          <strong>Guardian</strong>
          <button className="nav-close" onClick={() => setMobileNav(false)} aria-label="Fechar menu">×</button>
        </div>
        <div className="mobile-nav-list">
          {navSections.map((section) => (
            <div className="mobile-nav-section" key={section.label}>
              <span className="nav-section-label">{section.label}</span>
              {section.items.map((item) => (
                <a key={item.href} href={item.href} className={isActive(item.href) ? "active" : ""} onClick={() => setMobileNav(false)}>
                  <span className="nav-icon"><NavIcon name={item.icon} /></span>
                  <span className="nav-label">{item.label}</span>
                </a>
              ))}
            </div>
          ))}
        </div>
        <div className="mobile-nav-foot">
          {companies.length > 1 && (
            <select
              className="company-switcher"
              value={currentCompany?.tenantId ?? ""}
              onChange={(event) => void switchCompany(event.target.value)}
              aria-label="Empresa"
            >
              {companies.map((company) => (
                <option key={company.tenantId} value={company.tenantId}>
                  {company.tenantName || company.slug || "Empresa atual"}
                </option>
              ))}
            </select>
          )}
          {switchError && <small className="company-switch-error" role="alert">{switchError}</small>}
          <span>{user?.displayName}</span>
          <button className="topbar-logout" onClick={() => { session.clear(); location.hash = "#/login"; }}>Sair</button>
        </div>
      </nav>
      <main><div className="global-toolbar"><button className="theme-toggle" onClick={toggleTheme}>{theme === "dark" ? "☀ Tema claro" : "☾ Tema escuro"}</button></div>{children}</main>
      {widget?.supportWidgetEnabled && widget.supportWidgetHtml && <iframe className="support-widget" title="Suporte" srcDoc={widget.supportWidgetHtml} sandbox="allow-scripts allow-forms allow-popups" referrerPolicy="no-referrer" />}
    </div>
  );
}

function Login({ onSignedIn }: { onSignedIn?: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api.login(email, password, totp);
      session.save(result.accessToken, result.user);
      location.hash = "#/";
      // Não depender só do hashchange: se o hash já for "#/", o evento não
      // dispara e a tela ficaria no login até um refresh.
      onSignedIn?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha no login.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login-page">
      <section className="login-copy">
        <div className="login-logo">
          <img src="/brand/dbarros-horizontal.png" alt="Guardian Insight" />
          <strong>Guardian</strong>
        </div>
        <h1>Visibilidade real sobre cada equipamento.</h1>
        <p>
          Telemetria, políticas e evidências operacionais em uma única central
          segura.
        </p>
      </section>
      <section className="login-card">
        <form onSubmit={submit}>
          <p className="eyebrow">ACESSO SEGURO</p>
          <h2>Entrar no painel</h2>
          <label>
            E-mail
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
            />
          </label>
          <label>
            Senha
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <label>
            Código MFA <small>(se habilitado)</small>
            <input
              className="totp-input"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
              autoComplete="one-time-code"
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button disabled={busy}>{busy ? "Entrando…" : "Entrar"}</button>
          <small>Protegido por senha, TOTP e limitação de tentativas.</small>
        </form>
      </section>
    </div>
  );
}

function MfaEnrollment() {
  const [setup, setSetup] = useState<{ secret: string; otpAuthUri: string }>();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .setupMfa()
      .then(setSetup)
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Falha ao preparar o MFA."),
      );
  }, []);
  const confirm = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.confirmMfa(code);
      const refreshed = await api.refresh();
      session.save(refreshed.accessToken, refreshed.user);
      window.location.reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Código inválido. Tente novamente.",
      );
      setBusy(false);
    }
  };
  return (
    <div className="login-shell">
      <section className="login-card">
        <p className="eyebrow">SEGURANÇA OBRIGATÓRIA</p>
        <h1>Ative a autenticação em duas etapas</h1>
        <p className="muted">
          Seu perfil tem acesso administrativo, então o segundo fator é
          obrigatório. Cadastre o segredo abaixo no aplicativo autenticador e
          confirme com o código de seis dígitos.
        </p>
        {error && <p className="error">{error}</p>}
        {!setup && !error && <p className="muted">Preparando o segredo…</p>}
        {setup && (
          <form onSubmit={confirm}>
            <label>
              Segredo
              <input readOnly value={setup.secret} />
            </label>
            <label>
              Código de 6 dígitos
              <input
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                autoComplete="one-time-code"
              />
            </label>
            <button disabled={busy}>
              {busy ? "Confirmando…" : "Confirmar e entrar"}
            </button>
            <small className="muted">
              Também é possível importar por: {setup.otpAuthUri}
            </small>
          </form>
        )}
        <button
          className="link-button"
          onClick={() => {
            session.clear();
            window.location.reload();
          }}
        >
          Sair
        </button>
      </section>
    </div>
  );
}

function SecurityPage() {
  const [setup, setSetup] = useState<{ secret: string; otpAuthUri: string }>();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const prepare = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      setSetup(await api.setupMfa());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao preparar MFA.");
    } finally {
      setBusy(false);
    }
  };
  const confirm = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.confirmMfa(code);
      setSetup(undefined);
      setCode("");
      setMessage(
        "MFA habilitado. Os próximos logins exigirão o código do aplicativo.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Código inválido.");
    } finally {
      setBusy(false);
    }
  };
  const disable = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.disableMfa(password, disableCode);
      setPassword("");
      setDisableCode("");
      setMessage("MFA desabilitado.");
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Não foi possível desabilitar.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <header>
        <div>
          <p className="eyebrow">PROTEÇÃO DA CONTA</p>
          <h1>Segurança</h1>
          <p className="muted">
            Use um aplicativo autenticador para proteger o acesso ao painel.
          </p>
        </div>
      </header>
      {error && <p className="error">{error}</p>}
      {message && <p className="success notice">{message}</p>}
      <section className="security-grid">
        <article className="panel security-card">
          <h2>Habilitar autenticação em duas etapas</h2>
          <p className="muted">
            Compatível com Microsoft Authenticator, Google Authenticator,
            1Password e similares.
          </p>
          {setup ? (
            <form onSubmit={confirm}>
              <p>Adicione a conta usando a chave abaixo:</p>
              <code className="activation-key">{setup.secret}</code>
              <a className="authenticator-link" href={setup.otpAuthUri}>
                Abrir no aplicativo autenticador
              </a>
              <label>
                Código de 6 dígitos
                <input
                  className="totp-input"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required
                />
              </label>
              <button disabled={busy}>Confirmar e habilitar</button>
            </form>
          ) : (
            <button disabled={busy} onClick={prepare}>
              Preparar MFA
            </button>
          )}
        </article>
        <article className="panel security-card">
          <h2>Desabilitar MFA</h2>
          <p className="muted">
            Por segurança, confirme novamente sua senha e o código atual.
          </p>
          <form onSubmit={disable}>
            <label>
              Senha
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <label>
              Código atual
              <input
                className="totp-input"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={disableCode}
                onChange={(e) =>
                  setDisableCode(e.target.value.replace(/\D/g, ""))
                }
                autoComplete="one-time-code"
                required
              />
            </label>
            <button className="danger" disabled={busy}>
              Desabilitar MFA
            </button>
          </form>
        </article>
      </section>
    </>
  );
}

function Dashboard() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [layout, setLayout] = useState<"mosaic" | "table">("mosaic");
  const [filter, setFilter] = useState("");
  const [connection, setConnection] = useState("");
  const [tag, setTag] = useState("");
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const canManageKeys = can(session.user(), "key.manage");
  const visible = devices.filter(d => d.machineName.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) && (!connection || d.status === connection));
  const refresh = () =>
    api
      .devices(tag || undefined)
      .then(setDevices)
      .catch((e) => setError(e.message));
  useEffect(() => {
    void refresh();
  }, [tag]);
  useEffect(() => {
    api.deviceTags().then((result) => setTagOptions(result.tags)).catch(() => undefined);
  }, []);
  const online = devices.filter((d) => d.status === "online").length;
  return (
    <>
      <PageHeader
        eyebrow="VISÃO GERAL"
        title="Equipamentos"
        subtitle="Estado atual da sua infraestrutura monitorada."
        actions={
          <>
            <div className="live">
              <i /> Dados do último contato
            </div>
            {canManageKeys && (
              <button onClick={() => setShowAdd(true)}>
                + Adicionar equipamento
              </button>
            )}
          </>
        }
      />
      {error && <p className="error">{error}</p>}
      <section className="stats">
        <Metric label="Total" value={devices.length} />
        <Metric label="Online" value={online} tone="green" />
        <Metric label="Offline" value={devices.length - online} tone="orange" />
      </section>
      <FilterBar
        actions={
          <>
            <button type="button" className="secondary" onClick={() => setLayout(layout === "mosaic" ? "table" : "mosaic")}>{layout === "mosaic" ? "Ver tabela" : "Ver mosaico"}</button>
            <button type="button" className="secondary" onClick={() => void refresh()}>Atualizar</button>
          </>
        }
      >
        <label>Equipamento<input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Buscar pelo nome" /></label>
        <label>Conexão<select value={connection} onChange={e => setConnection(e.target.value)}><option value="">Todas</option><option value="online">Online</option><option value="offline">Offline</option></select></label>
        <label>Etiqueta<select value={tag} onChange={e => setTag(e.target.value)}><option value="">Todas</option>{tagOptions.map(t => <option key={t} value={t}>{t}</option>)}</select></label>
      </FilterBar>
      <section className="panel">
        <div className="panel-title">
          <h2>Dispositivos</h2>
          <span>{visible.length} de {devices.length} registrados</span>
        </div>
        {layout === "mosaic" ? <DeviceMosaic devices={visible} /> : <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Equipamento</th>
                <th>Etiquetas</th>
                <th>Conexão</th>
                <th>Controle</th>
                <th>Plano</th>
                <th>CPU</th>
                <th>Memória</th>
                <th>Disco livre</th>
                <th>Último contato</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => (location.hash = `#/devices/${d.id}`)}
                >
                  <td>
                    <strong>{d.machineName}</strong>
                    <small>{d.osVersion}</small>
                  </td>
                  <td>
                    {d.tags?.length
                      ? d.tags.map((t) => <span className="tag-chip" key={t}>{t}</span>)
                      : <small>—</small>}
                  </td>
                  <td>
                    <span className={`status ${d.status}`}>{d.status}</span>
                  </td>
                  <td>
                    <span className={`status lifecycle-${d.lifecycleState}`}>
                      {lifecycleLabel(d.lifecycleState)}
                    </span>
                  </td>
                  <td><strong>{d.planName}</strong><small>{d.screenRetentionDays} dias de telas</small></td>
                  <td>{percent(d.cpuPercent)}</td>
                  <td>{percent(d.memoryUsedPercent)}</td>
                  <td>{bytes(d.diskFreeBytes)}</td>
                  <td>
                    {d.lastSeenAt
                      ? new Date(d.lastSeenAt).toLocaleString("pt-BR")
                      : "Nunca"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {devices.length === 0 && (
            <div className="empty">
              <b>Nenhum equipamento ativado</b>
              <span>
                Use “Adicionar equipamento” para gerar uma chave segura.
              </span>
            </div>
          )}
        </div>}
      </section>
      {showAdd && <AddDeviceModal onClose={() => setShowAdd(false)} />}
    </>
  );
}

// Rótulo honesto para o gráfico: a API devolve até 24 h de telemetria, mas o
// equipamento pode ter ficado offline; melhor dizer o período realmente plotado.
const telemetryWindow = (points: Array<{ capturedAt: string }>) => {
  if (points.length < 2) return "últimas 24h";
  const hours =
    (Date.parse(points[points.length - 1].capturedAt) - Date.parse(points[0].capturedAt)) / 3_600_000;
  if (!Number.isFinite(hours) || hours <= 0) return "últimas 24h";
  return hours >= 23.5 ? "últimas 24h" : `últimas ${Math.max(1, Math.round(hours))}h`;
};

function TelemetryChart({
  points,
}: {
  points: Array<{ capturedAt: string; cpuPercent: number; memoryUsedPercent: number }>;
}) {
  if (!points.length) {
    return (
      <div className="empty">
        <b>Sem telemetria no período</b>
        <span>Os dados aparecem após o primeiro heartbeat do agente.</span>
      </div>
    );
  }
  const width = 720;
  const height = 220;
  const padLeft = 40;
  const padRight = 12;
  const padTop = 14;
  const padBottom = 26;
  const innerWidth = width - padLeft - padRight;
  const innerHeight = height - padTop - padBottom;
  const step = points.length > 1 ? innerWidth / (points.length - 1) : 0;
  const pointX = (index: number) => padLeft + (points.length > 1 ? index * step : innerWidth / 2);
  const pointY = (value: number) =>
    padTop + innerHeight - (Math.min(100, Math.max(0, value)) / 100) * innerHeight;
  const path = (key: "cpuPercent" | "memoryUsedPercent") =>
    points
      .map((point, index) => `${index ? "L" : "M"}${pointX(index).toFixed(1)},${pointY(point[key]).toFixed(1)}`)
      .join(" ");
  const areaPath = `${path("cpuPercent")} L${pointX(points.length - 1).toFixed(1)},${(padTop + innerHeight).toFixed(1)} L${pointX(0).toFixed(1)},${(padTop + innerHeight).toFixed(1)} Z`;
  const average = (key: "cpuPercent" | "memoryUsedPercent") =>
    points.reduce((total, point) => total + point[key], 0) / points.length;
  const timeLabel = (value: string) =>
    new Date(value).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const middle = points[Math.floor((points.length - 1) / 2)];
  const last = points[points.length - 1];
  return (
    <div className="telemetry-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Uso de CPU e memória no período">
        {[0, 25, 50, 75, 100].map((value) => (
          <g key={value}>
            <line
              className="chart-grid"
              x1={padLeft}
              x2={width - padRight}
              y1={pointY(value)}
              y2={pointY(value)}
            />
            <text className="chart-axis" x={padLeft - 8} y={pointY(value) + 4} textAnchor="end">
              {value}%
            </text>
          </g>
        ))}
        <path className="chart-area" d={areaPath} />
        <path className="chart-line memory" d={path("memoryUsedPercent")} />
        <path className="chart-line cpu" d={path("cpuPercent")} />
        <text className="chart-axis" x={padLeft} y={height - 8}>
          {timeLabel(points[0].capturedAt)}
        </text>
        <text className="chart-axis" x={padLeft + innerWidth / 2} y={height - 8} textAnchor="middle">
          {timeLabel(middle.capturedAt)}
        </text>
        <text className="chart-axis" x={width - padRight} y={height - 8} textAnchor="end">
          {timeLabel(last.capturedAt)}
        </text>
      </svg>
      <div className="chart-legend">
        <span>
          <i className="cpu" /> CPU {Math.round(last.cpuPercent)}% agora · média {Math.round(average("cpuPercent"))}%
        </span>
        <span>
          <i className="memory" /> Memória {Math.round(last.memoryUsedPercent)}% agora · média{" "}
          {Math.round(average("memoryUsedPercent"))}%
        </span>
      </div>
    </div>
  );
}

function DevicePage({ id }: { id: string }) {
  // Suporte cadastra e diagnostica, mas não lê capturas, atividade, eventos ou relatórios.
  const currentUser = session.user();
  const canSeeContent = can(currentUser, "content.read");
  const [detail, setDetail] = useState<DeviceDetail>();
  const [events, setEvents] = useState<DeviceEvent[]>([]);
  const [eventLevel, setEventLevel] = useState("");
  const [eventCategory, setEventCategory] = useState("");
  const [eventFrom, setEventFrom] = useState(localInput(new Date(Date.now() - 7 * 86400000)));
  const [eventTo, setEventTo] = useState(localInput(new Date()));
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [eventsBusy, setEventsBusy] = useState(false);
  const [incidents, setIncidents] = useState<DeviceIncident[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tagDraft, setTagDraft] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [tagMessage, setTagMessage] = useState("");
  const [plans, setPlans] = useState<Plan[]>([]);
  const canOperate = can(currentUser, "device.state");
  const canRevoke = can(currentUser, "device.manage");
  const canManageTags = can(currentUser, "tag.manage");
  const canEditPolicy = can(currentUser, "policy.manage");
  const refresh = () =>
    api
      .device(id)
      .then(setDetail)
      .catch((e) => setError(e.message));
  const changeState = async (state: Device["lifecycleState"]) => {
    if (
      state === "revoked" &&
      !confirm(
        "Revogar desativa o agente deste equipamento (dá para reativar depois). Continuar?",
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      await api.updateDeviceState(id, state);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao alterar estado.");
    } finally {
      setBusy(false);
    }
  };
  const removeDevice = async () => {
    if (!confirm("Remover este equipamento do cadastro? O histórico e as capturas serão excluídos.")) return;
    setBusy(true);
    setError("");
    try {
      await api.deleteDevice(id);
      location.hash = "#/";
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao remover equipamento.");
      setBusy(false);
    }
  };
  const loadEvents = async (submit?: FormEvent) => {
    submit?.preventDefault();
    setEventsBusy(true);
    try {
      const page = await api.deviceEvents(id, {
        level: eventLevel || undefined,
        category: eventCategory || undefined,
        from: new Date(eventFrom).toISOString(),
        to: new Date(eventTo).toISOString(),
        limit: 100,
      });
      setEvents(page.items);
      setEventsLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar eventos.");
    } finally {
      setEventsBusy(false);
    }
  };
  const addTag = () => {
    const value = tagInput.trim();
    if (!value) return;
    setTagMessage("");
    setTagDraft((current) =>
      current.some((existing) => existing.toLowerCase() === value.toLowerCase())
        ? current
        : [...current, value]);
    setTagInput("");
  };
  const saveTags = async () => {
    setTagMessage("");
    try {
      const result = await api.saveDeviceTags(id, tagDraft);
      setTagDraft(result.tags);
      setTagMessage("Etiquetas salvas.");
      refresh();
    } catch (e) {
      setTagMessage(e instanceof Error ? e.message : "Falha ao salvar etiquetas.");
    }
  };
  useEffect(() => {
    refresh();
    api.plans().then(setPlans).catch(() => undefined);
    if (!canSeeContent) return;
    const incidentTo = new Date();
    api
      .deviceIncidents(id, { from: new Date(incidentTo.getTime() - 7 * 86400000).toISOString(), to: incidentTo.toISOString() })
      .then(setIncidents)
      .catch(() => undefined);
  }, [id]);
  useEffect(() => {
    if (detail) setTagDraft(detail.device.tags ?? []);
  }, [detail]);
  if (error) return <p className="error">{error}</p>;
  if (!detail) return <p className="muted">Carregando…</p>;
  const d = detail.device;
  const companion = companionBadge(d);
  return (
    <>
      <a className="back" href="#/">
        ← Equipamentos
      </a>
      <PageHeader
        eyebrow="DETALHE DO EQUIPAMENTO"
        title={d.machineName}
        subtitle={`${d.osVersion} · Agente ${d.agentVersion}`}
        actions={
          <div className="device-controls">
          {canSeeContent && (
            <a className="button-link" href={`#/devices/${id}/timeline`}>
              Abrir timeline
            </a>
          )}
          <span className={`status ${d.status}`}>{d.status}</span>
          <span className={`status lifecycle-${d.lifecycleState}`}>
            {lifecycleLabel(d.lifecycleState)}
          </span>
          {d.status === "online" && detail.policy.screenshotsEnabled && companion && (
            <span className={`status ${companion.tone}`} title={companion.title}>
              {companion.label}
            </span>
          )}
          {canOperate && d.lifecycleState === "ready" && (
            <button
              disabled={busy}
              className="secondary"
              onClick={() => changeState("paused")}
            >
              Pausar
            </button>
          )}
          {((canOperate && d.lifecycleState === "paused") || (canRevoke && d.lifecycleState === "revoked")) && (
            <button disabled={busy} onClick={() => changeState("ready")}>
              Reativar
            </button>
          )}
          {canRevoke && d.lifecycleState !== "revoked" && (
            <button
              disabled={busy}
              className="danger"
              onClick={() => changeState("revoked")}
            >
              Revogar
            </button>
          )}
          {canRevoke && (
            <button disabled={busy} className="danger" onClick={removeDevice}>
              Remover do cadastro
            </button>
          )}
          </div>
        }
      />
      <section className="stats six">
        <Metric label="CPU" value={percent(d.cpuPercent)} />
        <Metric label="Memória" value={percent(d.memoryUsedPercent)} />
        <Metric label="Disco livre" value={bytes(d.diskFreeBytes)} />
        <Metric label="Uptime" value={duration(d.uptimeSeconds)} />
        <Metric label="Fila offline" value={`${d.bufferedSnapshotCount} · ${bytes(d.bufferedSnapshotBytes)}`} tone={d.bufferedSnapshotCount > 0 ? "orange" : "green"} />
        <Metric label="Conexão" value={`${d.status === "online" ? "Online" : "Offline"} · Proxy ${d.proxyMode}`} tone={d.status === "online" ? "green" : "orange"} />
      </section>
      <section className="panel plan-summary">
        <div>
          <p className="eyebrow">PLANO DO EQUIPAMENTO</p>
          <h2>{d.planName}</h2>
          <span className="muted">{d.screenRetentionDays} dias de telas · {d.operationalRetentionDays} dias de telemetria e eventos</span>
        </div>
        {session.user()?.platformRole === "platform_admin" && (
          <label>Alterar plano
            <select value={d.planId} disabled={busy} onChange={async (event) => {
              const selected = plans.find((plan) => plan.id === event.target.value);
              if (!selected || selected.id === d.planId) return;
              const downgrade = selected.screenRetentionDays < d.screenRetentionDays;
              if (downgrade && !confirm(`Ao mudar para ${selected.name}, capturas fora da nova retenção poderão ser excluídas no próximo ciclo. Continuar?`)) return;
              setBusy(true); setError("");
              try { await api.updateDevicePlan(id, selected.id); await refresh(); }
              catch (e) { setError(e instanceof Error ? e.message : "Falha ao alterar plano."); }
              finally { setBusy(false); }
            }}>
              {plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name} — {plan.screenRetentionDays} dias</option>)}
            </select>
          </label>
        )}
      </section>
      <InventoryPanel deviceId={id} />
      {canSeeContent && incidents.length > 0 && (
        <section className="panel">
          <div className="panel-title">
            <h2>Incidentes</h2>
            <span>{incidents.length} nos últimos 7 dias</span>
          </div>
          <div className="event-list">
            {incidents.map((incident) => (
              <article key={incident.id} className={`event-row level-${incident.severity}`}>
                <div>
                  <strong>{incident.title}</strong>
                  <small>{incident.kind.replaceAll("_", " ")} · {incident.eventCount} evento(s) relacionado(s)</small>
                </div>
                <span className="event-time">{new Date(incident.startedAt).toLocaleString("pt-BR")}</span>
              </article>
            ))}
          </div>
        </section>
      )}
      <details
        hidden={!canSeeContent}
        className="panel collapsible"
        onToggle={(event) => {
          if (event.currentTarget.open && !eventsLoaded) void loadEvents();
        }}
      >
        <summary>
          <h2>Eventos do Windows</h2>
          <span>{eventsLoaded ? `${events.length} eventos` : ""}</span>
        </summary>
        <form className="event-filters" onSubmit={loadEvents}>
          <label>
            Nível
            <select value={eventLevel} onChange={(e) => setEventLevel(e.target.value)}>
              <option value="">Todos</option>
              <option value="critical">Crítico</option>
              <option value="error">Erro</option>
              <option value="warning">Aviso</option>
              <option value="information">Informação</option>
            </select>
          </label>
          <label>
            Categoria
            <select value={eventCategory} onChange={(e) => setEventCategory(e.target.value)}>
              <option value="">Todas</option>
              <option value="crash">Crash</option>
              <option value="bsod">Tela azul</option>
              <option value="power">Energia</option>
              <option value="service">Serviço</option>
              <option value="disk">Disco</option>
              <option value="hardware">Hardware</option>
              <option value="update">Update</option>
              <option value="security">Segurança</option>
              <option value="network">Rede</option>
              <option value="boot">Boot</option>
              <option value="performance">Desempenho</option>
            </select>
          </label>
          <label>
            De
            <input type="datetime-local" value={eventFrom} onChange={(e) => setEventFrom(e.target.value)} />
          </label>
          <label>
            Até
            <input type="datetime-local" value={eventTo} onChange={(e) => setEventTo(e.target.value)} />
          </label>
          <button disabled={eventsBusy}>{eventsBusy ? "Carregando…" : "Filtrar"}</button>
        </form>
        <div className="event-list">
          {events.map((event) => (
            <article key={event.id} className={`event-row level-${event.level}`}>
              <div>
                <strong>{event.title}</strong>
                <small>{event.subject ? `${event.subject} · ` : ""}{event.provider} · evento {event.eventId} · {event.channel}{event.source === "import" ? " · importado" : ""}</small>
                {event.message && <details><summary>Detalhes</summary><p>{event.message}</p></details>}
              </div>
              <span className="event-time">{new Date(event.occurredAt).toLocaleString("pt-BR")}</span>
            </article>
          ))}
          {eventsLoaded && !events.length && (
            <div className="empty">
              <b>Nenhum evento relevante</b>
              <span>Ajuste os filtros ou o período consultado.</span>
            </div>
          )}
        </div>
      </details>
      <section className="panel tag-panel">
        <div className="panel-title">
          <h2>Etiquetas</h2>
          <span>{tagDraft.length} de 20</span>
        </div>
        <div className="tag-list">
          {tagDraft.length
            ? tagDraft.map((item) => (
                <span className="tag-chip" key={item}>
                  {item}
                  {canManageTags && (
                    <button
                      className="tag-remove"
                      aria-label={`Remover ${item}`}
                      onClick={() => setTagDraft((current) => current.filter((tag) => tag !== item))}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))
            : <small>Sem etiquetas.</small>}
        </div>
        {canManageTags && (
          <div className="tag-editor">
            <input
              value={tagInput}
              maxLength={40}
              placeholder="Ex.: Marketing"
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag();
                }
              }}
            />
            <button className="secondary" onClick={addTag}>Adicionar</button>
            <button onClick={() => void saveTags()}>Salvar</button>
          </div>
        )}
        {tagMessage && <p className="muted">{tagMessage}</p>}
      </section>
      <section className="grid">
        <PolicyEditor deviceId={id} policy={detail.policy} onSaved={refresh} canEdit={canEditPolicy} />
        <article className="panel">
          <div className="panel-title">
            <h2>Atividade</h2>
            <span>
              {detail.telemetry.length} amostras · {telemetryWindow(detail.telemetry)}
            </span>
          </div>
          <TelemetryChart points={detail.telemetry} />
        </article>
      </section>
      {canSeeContent && <EventImportPanel deviceId={id} canImport={canRevoke} />}
    </>
  );
}

function AddDeviceModal({ onClose }: { onClose: () => void }) {
  const [label, setLabel] = useState("Novo equipamento");
  const [key, setKey] = useState("");
  const [plans, setPlans] = useState<Plan[]>([]);
  const [keys, setKeys] = useState<ActivationKey[]>([]);
  const [planId, setPlanId] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [generatedExpiresAt, setGeneratedExpiresAt] = useState("");
  const [error, setError] = useState("");
  const refreshKeys = () => api.activationKeys().then(setKeys).catch((e) => setError(e.message));
  useEffect(() => {
    api.plans().then((items) => { setPlans(items); setPlanId(items[0]?.id ?? ""); }).catch((e) => setError(e.message));
    void refreshKeys();
  }, []);
  const create = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const created = await api.createActivationKey(label, maxUses, planId);
      setKey(created.key ?? "");
      setGeneratedExpiresAt(created.expiresAt);
      await refreshKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao criar chave.");
    }
  };
  const copy = () => navigator.clipboard.writeText(key);
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose}>
          ×
        </button>
        <p className="eyebrow">ATIVAÇÃO DO AGENTE</p>
        <h2>Adicionar equipamento</h2>
        <p className="muted">
          Instale o agente no computador e informe a chave de ativação para conectá-lo.
        </p>
        <a
          className="button-link agent-download"
          href="/downloads/DBGuardian-Agent-Setup-latest.exe"
        >
          Baixar instalador do agente (Windows) · v{__APP_VERSION__}
        </a>
        <p className="muted">
          Implantação em massa (GPO/silencioso)?{" "}
          <a href="/downloads/DBGuardian-Agent-latest-win-x64.zip">Baixe o pacote .zip</a>.
        </p>
        {key ? (
          <>
            <p className="muted">
              Esta chave é exibida uma única vez, permite {maxUses} ativação{maxUses === 1 ? "" : "ões"} e expira em {new Date(generatedExpiresAt).toLocaleString("pt-BR")}.
            </p>
            <code className="activation-key">{key}</code>
            <button onClick={copy}>Copiar chave</button>
            <button className="secondary" onClick={onClose}>
              Concluir
            </button>
          </>
        ) : (
          <form onSubmit={create}>
            <label>
              Identificação
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
              />
            </label>
            <label>Plano<select value={planId} onChange={(e) => setPlanId(e.target.value)} required>{plans.map((plan) => <option value={plan.id} key={plan.id}>{plan.name} · {plan.screenRetentionDays} dias de telas</option>)}</select></label>
            <label>Quantidade de instalações<input type="number" min="1" max="10000" value={maxUses} onChange={(e) => setMaxUses(Number(e.target.value))} required /></label>
            {maxUses > 1 && <p className="muted">Chave para implantação em massa. Cada computador receberá sua própria credencial.</p>}
            {error && <p className="error">{error}</p>}
            <button>Gerar chave de ativação</button>
          </form>
        )}
        {keys.length > 0 && <div className="activation-list"><h3>Chaves recentes</h3>{keys.slice(0, 8).map((item) => {
          const active = !item.revokedAt && new Date(item.expiresAt) > new Date() && item.usedCount < item.maxUses;
          return <div key={item.id}><span><strong>{item.label || "Sem identificação"}</strong><small>{item.planName} · {item.usedCount}/{item.maxUses} usos · até {new Date(item.expiresAt).toLocaleString("pt-BR")}</small></span>{active && <button className="small-button danger" onClick={async () => { await api.revokeActivationKey(item.id); await refreshKeys(); }}>Revogar</button>}</div>;
        })}</div>}
      </section>
    </div>
  );
}

function PolicyEditor({
  deviceId,
  policy,
  onSaved,
  canEdit,
}: {
  deviceId: string;
  policy: Policy;
  onSaved: () => void;
  canEdit: boolean;
}) {
  const [draft, setDraft] = useState(policy);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");
  const update = (key: keyof Policy, value: number | boolean | string[]) =>
    setDraft((p) => ({ ...p, [key]: value }));
  const save = async () => {
    await api.updatePolicy(deviceId, draft);
    setEditing(false);
    setMessage("Política salva.");
    onSaved();
  };
  return (
    <article className="panel policy-panel">
      <div className="panel-title">
        <h2>Política aplicada</h2>
        {canEdit && (
          <button className="small-button" onClick={() => setEditing(!editing)}>
            {editing ? "Cancelar" : "Editar"}
          </button>
        )}
      </div>
      {editing && canEdit ? (
        <div className="policy-form">
          <label>
            Heartbeat (s)
            <input
              type="number"
              min="15"
              max="3600"
              value={draft.heartbeatSeconds}
              onChange={(e) =>
                update("heartbeatSeconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Telemetria (s)
            <input
              type="number"
              min="15"
              max="3600"
              value={draft.telemetrySeconds}
              onChange={(e) =>
                update("telemetrySeconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Captura periódica (s)
            <input
              type="number"
              min="30"
              max="86400"
              value={draft.screenshotSeconds}
              onChange={(e) =>
                update("screenshotSeconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Verificação visual (s)
            <input
              type="number"
              min="1"
              max="300"
              value={draft.visualCheckSeconds}
              onChange={(e) =>
                update("visualCheckSeconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Intervalo mínimo (s)
            <input
              type="number"
              min="1"
              max="300"
              value={draft.minimumCaptureIntervalSeconds}
              onChange={(e) =>
                update("minimumCaptureIntervalSeconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Alteração visual mínima (%)
            <input
              type="number"
              min="1"
              max="100"
              step="0.5"
              value={draft.visualChangeThresholdPercent}
              onChange={(e) =>
                update("visualChangeThresholdPercent", Number(e.target.value))
              }
            />
          </label>
          <label>
            Atraso dos gatilhos (ms)
            <input
              type="number"
              min="0"
              max="10000"
              value={draft.triggerDelayMilliseconds}
              onChange={(e) =>
                update("triggerDelayMilliseconds", Number(e.target.value))
              }
            />
          </label>
          <label>
            Qualidade JPEG
            <input
              type="number"
              min="35"
              max="95"
              value={draft.jpegQuality}
              onChange={(e) => update("jpegQuality", Number(e.target.value))}
            />
          </label>
          <label>
            Debounce da atividade (ms)
            <input type="number" min="100" max="10000" value={draft.activityDebounceMilliseconds}
              onChange={(e) => update("activityDebounceMilliseconds", Number(e.target.value))} />
          </label>
          <label>
            Inventário (segundos)
            <input type="number" min="3600" max="604800" step="3600" value={draft.inventorySeconds ?? 86400}
              onChange={(e) => update("inventorySeconds", Number(e.target.value))} />
          </label>
          <label>
            Processos protegidos (separados por vírgula)
            <input
              value={draft.excludedProcesses.join(", ")}
              onChange={(e) =>
                update(
                  "excludedProcesses",
                  e.target.value
                    .split(",")
                    .map((value) => value.trim())
                    .filter(Boolean),
                )
              }
            />
          </label>
          <div className="policy-checks">
            <label className="check">
              <input type="checkbox" checked={draft.activityTrackingEnabled}
                onChange={(e) => update("activityTrackingEnabled", e.target.checked)} />{" "}
              Metadados de atividade
            </label>
            <label className="check">
              <input type="checkbox" checked={draft.windowTitlesEnabled}
                onChange={(e) => update("windowTitlesEnabled", e.target.checked)} />{" "}
              Títulos de janela (sensível)
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={draft.screenshotsEnabled}
                onChange={(e) => update("screenshotsEnabled", e.target.checked)}
              />{" "}
              Capturas habilitadas
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={draft.captureOnClick}
                onChange={(e) => update("captureOnClick", e.target.checked)}
              />{" "}
              Após clique
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={draft.captureOnEnter}
                onChange={(e) => update("captureOnEnter", e.target.checked)}
              />{" "}
              Após Enter
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={draft.captureOnWindowChange}
                onChange={(e) =>
                  update("captureOnWindowChange", e.target.checked)
                }
              />{" "}
              Ao trocar janela
            </label>
          </div>
          <button onClick={save}>Salvar política</button>
        </div>
      ) : (
        <dl>
          <div>
            <dt>Metadados de atividade</dt>
            <dd>{policy.activityTrackingEnabled ? `Ativos · ${policy.windowTitlesEnabled ? "com títulos" : "sem títulos"}` : "Desativados"}</dd>
          </div>
          <div>
            <dt>Captura periódica</dt>
            <dd>
              {policy.screenshotsEnabled
                ? `${policy.screenshotSeconds}s`
                : "Desativada"}
            </dd>
          </div>
          <div>
            <dt>Detecção visual</dt>
            <dd>
              {policy.visualCheckSeconds}s ·{" "}
              {policy.visualChangeThresholdPercent}%
            </dd>
          </div>
          <div>
            <dt>Gatilhos</dt>
            <dd>
              {[
                policy.captureOnClick && "clique",
                policy.captureOnEnter && "Enter",
                policy.captureOnWindowChange && "janela",
              ]
                .filter(Boolean)
                .join(", ") || "Nenhum"}
            </dd>
          </div>
          <div>
            <dt>Intervalo mínimo</dt>
            <dd>{policy.minimumCaptureIntervalSeconds}s</dd>
          </div>
          <div>
            <dt>Qualidade</dt>
            <dd>{policy.jpegQuality}%</dd>
          </div>
          <div>
            <dt>Retenção efetiva do plano</dt>
            <dd>{policy.snapshotRetentionDays} dias</dd>
          </div>
          {message && <p className="success">{message}</p>}
        </dl>
      )}
    </article>
  );
}

function TimelinePage({ deviceId }: { deviceId: string }) {
  const requestedAt = new URLSearchParams(location.hash.split("?", 2)[1] ?? "").get("at");
  const center = requestedAt ? new Date(requestedAt) : new Date();
  const [from, setFrom] = useState(localInput(new Date(center.getTime() - (requestedAt ? 900000 : 86400000))));
  const [to, setTo] = useState(localInput(new Date(center.getTime() + (requestedAt ? 900000 : 0))));
  const [items, setItems] = useState<SnapshotView[]>([]);
  const [gaps, setGaps] = useState<TimelineGap[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [selected, setSelected] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [context, setContext] = useState<ActivityEvent | null | undefined | "error">(undefined);
  const [retry, setRetry] = useState(0);
  const [openWindows, setOpenWindows] = useState<{ process: string; title: string }[] | null>(null);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [activityFilter, setActivityFilter] = useState("");
  const windowsCache = useRef(new Map<string, { process: string; title: string }[] | null>());
  const contextCache = useRef(new Map<string, ActivityEvent | null>());
  const timelineTrackRef = useRef<HTMLDivElement>(null);
  const scrollTrack = (direction: number) => {
    const track = timelineTrackRef.current;
    if (!track) return;
    // Passo derivado do item real (200px + gap), em vez de um valor fixo que
    // diverge se o CSS mudar.
    const first = track.firstElementChild as HTMLElement | null;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 10;
    const step = (first?.offsetWidth ?? 200) + gap;
    track.scrollBy({ left: direction * step, behavior: "smooth" });
  };
  useEffect(() => {
    const track = timelineTrackRef.current;
    track?.querySelectorAll("button")[selected]?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [selected]);
  const retryContext = () => {
    const snapshot = items[selected];
    if (snapshot) {
      contextCache.current.delete(snapshot.id);
      windowsCache.current.delete(snapshot.id);
    }
    setContext(undefined);
    setOpenWindows(null);
    setRetry((value) => value + 1);
  };
  const openActivity = (event: ActivityEvent) => {
    const index = event.nearestSnapshotId
      ? items.findIndex((item) => item.id === event.nearestSnapshotId)
      : -1;
    if (index >= 0) {
      setSelected(index);
      setPlaying(false);
    }
  };
  // Os termos são procurados na string combinada (processo + título da janela), e todos
  // precisam aparecer nela: "chrome google" casa processo chrome + janela com "google",
  // mesmo que as palavras venham de campos diferentes.
  const filterTerms = activityFilter.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filteredActivity = filterTerms.length
    ? activity.filter((event) => {
        const haystack = `${event.processName ?? ""} ${event.windowTitle ?? ""}`.toLowerCase();
        return filterTerms.every((term) => haystack.includes(term));
      })
    : activity;

  useEffect(() => {
    const snapshot = items[selected];
    if (!snapshot) {
      setContext(undefined);
      setOpenWindows(null);
      return;
    }
    if (contextCache.current.has(snapshot.id)) {
      setContext(contextCache.current.get(snapshot.id) ?? null);
      setOpenWindows(windowsCache.current.get(snapshot.id) ?? null);
      return;
    }
    let cancelled = false;
    setContext(undefined);
    setOpenWindows(null);
    const at = new Date(snapshot.capturedAt).getTime();
    const timer = window.setTimeout(() => {
      api
        .searchActivity({
          deviceId,
          from: new Date(at - 120_000).toISOString(),
          to: new Date(at + 30_000).toISOString(),
        })
        .then((page) => {
          const nearest =
            page.items
              .filter((item) => item.processName || item.windowTitle)
              .sort(
                (a, b) =>
                  Math.abs(new Date(a.agentTimestamp).getTime() - at) -
                  Math.abs(new Date(b.agentTimestamp).getTime() - at),
              )[0] ?? null;
          const listEvent = page.items
            .filter((item) => item.eventType === "window_list" && item.attributes?.windows)
            .sort(
              (a, b) =>
                Math.abs(new Date(a.agentTimestamp).getTime() - at) -
                Math.abs(new Date(b.agentTimestamp).getTime() - at),
            )[0];
          let windows: { process: string; title: string }[] | null = null;
          if (listEvent?.attributes?.windows) {
            try {
              const parsed: unknown = JSON.parse(listEvent.attributes.windows);
              if (Array.isArray(parsed)) {
                windows = parsed
                  .filter(
                    (entry): entry is { process?: unknown; title?: unknown } =>
                      entry !== null && typeof entry === "object",
                  )
                  .map((entry) => ({
                    process: String(entry.process ?? ""),
                    title: String(entry.title ?? ""),
                  }))
                  .filter((entry) => entry.process.length > 0);
              }
            } catch {
              windows = null;
            }
          }
          contextCache.current.set(snapshot.id, nearest);
          windowsCache.current.set(snapshot.id, windows);
          if (!cancelled) {
            setContext(nearest);
            setOpenWindows(windows);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setContext("error");
            setOpenWindows(null);
          }
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [items, selected, deviceId, retry]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async (next?: string) => {
    setBusy(true);
    setError("");
    try {
      const page = await api.timeline(
        deviceId,
        new Date(from).toISOString(),
        new Date(to).toISOString(),
        next,
      );
      const views = await Promise.all(
        page.items.map((item) => api.snapshotView(deviceId, item.id)),
      );
      setItems((previous) => (next ? [...previous, ...views] : views));
      setGaps(page.offlinePeriods);
      setCursor(page.nextCursor);
      if (!next) {
        setSelected(0);
        // Lista lateral: eventos de atividade do mesmo período das capturas.
        // Uma consulta por carregamento (não por seleção) para não pesar no rate limit.
        const times = views
          .map((view) => new Date(view.capturedAt).getTime())
          .filter((value) => Number.isFinite(value));
        if (times.length > 0) {
          const newest = Math.max(...times);
          const oldest = Math.max(Math.min(...times), newest - 31 * 86400000);
          api
            .searchActivity({
              deviceId,
              from: new Date(oldest).toISOString(),
              to: new Date(newest + 1000).toISOString(),
              limit: 200,
            })
            .then((result) =>
              setActivity(result.items.filter((item) => item.eventType !== "window_list")),
            )
            .catch(() => setActivity([]));
        } else {
          // Sem capturas no período: limpar a lista evita mostrar os eventos do
          // período anterior, o que pareceria atividade "no período vazio".
          setActivity([]);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar timeline.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    void load();
  }, [deviceId]);
  useEffect(() => {
    if (!playing || items.length < 2) return;
    const timer = window.setInterval(
      () => setSelected((index) => (index + 1 < items.length ? index + 1 : 0)),
      2000 / speed,
    );
    return () => clearInterval(timer);
  }, [playing, speed, items.length]);
  const current = items[selected];
  return (
    <>
      <a className="back" href={`#/devices/${deviceId}`}>
        ← Equipamento
      </a>
      <PageHeader
        eyebrow="DVR DE EVIDÊNCIAS"
        title="Timeline"
        subtitle="Navegue pelas capturas sem carregar todo o histórico."
      />
      <FilterBar
        onSubmit={() => {
          // O filtro é do período anterior; limpar evita confundir "sem atividade"
          // com "esqueci o filtro ligado".
          setActivityFilter("");
          void load();
        }}
        actions={
          <button type="submit" disabled={busy}>
            Aplicar período
          </button>
        }
      >
        <label>
          Início
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label>
          Fim
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
      </FilterBar>
      {error && <p className="error">{error}</p>}
      {gaps.length > 0 && (
        <section className="offline-strip">
          <strong>Períodos offline detectados</strong>
          {gaps.map((g) => (
            <span key={g.startedAt}>
              {new Date(g.startedAt).toLocaleString("pt-BR")} —{" "}
              {new Date(g.endedAt).toLocaleString("pt-BR")}
            </span>
          ))}
        </section>
      )}
      {current && (
        <section className="dvr">
          <div className="viewer">
            <img
              src={current.downloadUrl}
              alt={`Captura de ${new Date(current.capturedAt).toLocaleString("pt-BR")}`}
            />
            <div className="viewer-meta">
              <strong>
                {new Date(current.capturedAt).toLocaleString("pt-BR")}
              </strong>
              <span>
                {reasonLabel(current.captureReason)} · {current.width}×
                {current.height} ·{" "}
                {bytes(current.contentLength)}
              </span>
              <div className="viewer-actions">
                <a
                  className="small-button"
                  href={current.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Baixar captura
                </a>
                <div className="viewer-context">
                  <span>Em uso no momento</span>
                  {context === undefined ? (
                    <small>Carregando…</small>
                  ) : context === "error" ? (
                    <small>
                      Não foi possível carregar a atividade.{" "}
                      <button className="link-button" onClick={retryContext}>
                        Tentar de novo
                      </button>
                    </small>
                  ) : context === null ? (
                    <small>Sem atividade registrada neste horário.</small>
                  ) : (
                    <strong>
                      {context.processName ?? "—"}
                      {context.windowTitle ? ` · ${context.windowTitle}` : ""}
                    </strong>
                  )}
                  {openWindows && openWindows.length > 0 && (
                    <details className="open-windows">
                      <summary>Janelas abertas ({openWindows.length})</summary>
                      <ul>
                        {openWindows.map((window, index) => (
                          <li key={`${window.process}-${index}`}>
                            <strong>{window.process}</strong>
                            {window.title ? ` · ${window.title}` : ""}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              </div>
            </div>
          </div>
          <aside className="activity-rail">
            <div className="rail-head">
              <strong>Atividade</strong>
              <span>{filteredActivity.length}</span>
            </div>
            <input
              className="rail-filter"
              value={activityFilter}
              onChange={(e) => setActivityFilter(e.target.value)}
              placeholder="Filtrar processo ou janela"
              aria-label="Filtrar eventos de atividade"
            />
            <div className="rail-list">
              {filteredActivity.map((event) => {
                const index = event.nearestSnapshotId
                  ? items.findIndex((item) => item.id === event.nearestSnapshotId)
                  : -1;
                return (
                  <button
                    key={event.id}
                    className={index >= 0 && index === selected ? "selected" : ""}
                    onClick={() => openActivity(event)}
                    title={event.windowTitle ?? undefined}
                  >
                    <time>{new Date(event.agentTimestamp).toLocaleTimeString("pt-BR")}</time>
                    <strong>{event.processName ?? event.eventType.replaceAll("_", " ")}</strong>
                    <small>{event.windowTitle ?? ""}</small>
                  </button>
                );
              })}
              {!filteredActivity.length && (
                <p className="muted rail-empty">
                  {activity.length
                    ? "Nenhum evento corresponde ao filtro."
                    : "Sem atividade no período."}
                </p>
              )}
            </div>
          </aside>
          <div className="playback">
            <button
              className="secondary"
              onClick={() => setSelected(Math.max(0, selected - 1))}
            >
              ← Anterior
            </button>
            <button onClick={() => setPlaying((value) => !value)}>
              {playing ? "Pausar" : "Reproduzir"}
            </button>
            <button
              className="secondary"
              onClick={() =>
                setSelected(Math.min(items.length - 1, selected + 1))
              }
            >
              Próxima →
            </button>
            <label>
              Velocidade
              <select
                value={speed}
                onChange={(e) => setSpeed(Number(e.target.value))}
              >
                <option value="0.5">0,5×</option>
                <option value="1">1×</option>
                <option value="2">2×</option>
                <option value="4">4×</option>
              </select>
            </label>
          </div>
        </section>
      )}
      <section className="panel timeline-panel">
        <div className="panel-title">
          <h2>Capturas</h2>
          <span>{items.length} capturas carregadas</span>
        </div>
        {items.length ? (
          <div className="carousel">
            <button className="carousel-nav" onClick={() => scrollTrack(-1)} aria-label="Capturas anteriores">‹</button>
            <div className="carousel-track" ref={timelineTrackRef}>
              {items.map((item, index) => (
                <button
                  key={item.id}
                  className={selected === index ? "selected" : ""}
                  onClick={() => {
                    setSelected(index);
                    setPlaying(false);
                  }}
                >
                  <img loading="lazy" src={item.downloadUrl} alt="" />
                  <span>{new Date(item.capturedAt).toLocaleTimeString("pt-BR")}</span>
                </button>
              ))}
            </div>
            <button className="carousel-nav" onClick={() => scrollTrack(1)} aria-label="Próximas capturas">›</button>
          </div>
        ) : (
          <div className="empty">
            <b>Nenhuma captura no período</b>
            <span>Ajuste as datas ou aguarde o próximo envio.</span>
          </div>
        )}
        {cursor && (
          <div className="load-more">
            <button disabled={busy} onClick={() => load(cursor)}>
              Carregar capturas anteriores
            </button>
          </div>
        )}
      </section>
    </>
  );
}

function UsersPage() {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [passwordUser, setPasswordUser] = useState<ManagedUser>();
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const currentUser = session.user();
  // Revenda é cadastro de plataforma: só o Master fora de escopo cria esse tipo de
  // usuário, e o cadastro precisa apontar qual revenda ele representa.
  const canManagePartners =
    currentUser?.platformRole === "platform_admin" &&
    !isScopedSession(session.token(), currentUser) &&
    can(currentUser, "user.manage");
  const [partners, setPartners] = useState<Partner[]>([]);
  // Empresas adicionais: concessão vale fora do tenant do token, então só o Master
  // sem escopo atribui (o endpoint devolve 403 para os demais).
  const canAssignCompanies = canManagePartners;
  const [companyOptions, setCompanyOptions] = useState<Tenant[]>([]);
  const [companiesUser, setCompaniesUser] = useState<ManagedUser>();
  const pagedUsers = usePagedList(users);
  const refresh = () =>
    api
      .users()
      .then(setUsers)
      .catch((e) => setError(e.message));
  useEffect(() => {
    void refresh();
  }, []);
  useEffect(() => {
    if (canManagePartners) api.partners().then(setPartners).catch(() => undefined);
  }, [canManagePartners]);
  useEffect(() => {
    if (canAssignCompanies) api.tenants().then(setCompanyOptions).catch(() => undefined);
  }, [canAssignCompanies]);
  const toggle = async (user: ManagedUser) => {
    setError("");
    try {
      await api.updateUser({ ...user, isActive: !user.isActive });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao atualizar usuário.");
    }
  };
  const resetMfa = async (user: ManagedUser) => {
    if (!confirm(`Remover o MFA de ${user.email}?`)) return;
    try {
      await api.resetUserMfa(user.id);
      setMessage("MFA removido e evento registrado na auditoria.");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao remover MFA.");
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="GESTÃO COMERCIAL"
        title="Usuários"
        subtitle="Controle quem acessa cada empresa e o nível de permissão."
        actions={<button onClick={() => setShowAdd(true)}>+ Novo usuário</button>}
      />
      <section className="panel">
        <div className="panel-title">
          <h2>Níveis de acesso</h2>
        </div>
        <ul className="role-legend">
          <li><b>ADM da empresa</b> — controle total da empresa e dos usuários.</li>
          <li><b>Administrador</b> — opera equipamentos, políticas, chaves e auditoria; não gerencia usuários.</li>
          <li><b>User</b> — consulta equipamentos, timeline, eventos e relatórios; não altera nada.</li>
          <li><b>Suporte</b> — cadastra e diagnostica equipamentos, sem acesso a capturas, atividade ou relatórios.</li>
          {partners.length > 0 && <li><b>Revenda — ADM / Suporte</b> — equipe da revenda na carteira de empresas dela; o ADM vê conteúdo e auditoria, o Suporte não.</li>}
        </ul>
        <p className="muted">
          O suporte Guardian Insight acessa uma empresa temporariamente, com leitura e pausa/reativação de
          equipamentos, mediante concessão auditada. Revogar equipamento e editar política são
          exclusivos de proprietário e administrador.
        </p>
      </section>
      {error && <p className="error">{error}</p>}
      {message && <p className="success notice">{message}</p>}
      <section className="panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Nível</th>
                {partners.length > 0 && <th>Revenda</th>}
                <th>MFA</th>
                <th>Estado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {pagedUsers.pageItems.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.displayName}</strong>
                    <small>{user.email}</small>
                  </td>
                  <td>
                    <span className={`role role-${user.role}`}>
                      {roleLabel(user.role)}
                    </span>
                  </td>
                  {partners.length > 0 && (
                    <td>{partners.find((partner) => partner.id === user.partnerId)?.name ?? "—"}</td>
                  )}
                  <td>{user.mfaEnabled ? "Ativo" : "Não configurado"}</td>
                  <td>{user.isActive ? "Ativo" : "Desativado"}</td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="small-button"
                        onClick={() => toggle(user)}
                      >
                        {user.isActive ? "Desativar" : "Ativar"}
                      </button>
                      <button
                        className="small-button"
                        onClick={() => setPasswordUser(user)}
                      >
                        Nova senha
                      </button>
                      {user.mfaEnabled && (
                        <button
                          className="small-button danger"
                          onClick={() => resetMfa(user)}
                        >
                          Remover MFA
                        </button>
                      )}
                      {canAssignCompanies && !user.role.startsWith("partner_") && (
                        <button
                          className="small-button"
                          onClick={() => setCompaniesUser(user)}
                        >
                          Empresas
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ListControls page={pagedUsers.page} pages={pagedUsers.pages} size={pagedUsers.size} total={pagedUsers.total} onSize={pagedUsers.changeSize} onPrev={pagedUsers.prev} onNext={pagedUsers.next} />
      </section>
      {companiesUser && (
        <UserCompaniesModal
          user={companiesUser}
          companies={companyOptions}
          onClose={() => setCompaniesUser(undefined)}
          onSaved={() => {
            setCompaniesUser(undefined);
            setMessage("Empresas do usuário atualizadas e auditadas.");
            void refresh();
          }}
        />
      )}
      {showAdd && (
        <AddUserModal
          partners={partners}
          companies={companyOptions}
          canAssignOwner={currentUser?.platformRole === "platform_admin" || currentUser?.role === "owner" || currentUser?.role === "admin"}
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            refresh();
          }}
        />
      )}
      {passwordUser && (
        <ResetPasswordModal
          user={passwordUser}
          onClose={() => setPasswordUser(undefined)}
          onSaved={() => {
            setPasswordUser(undefined);
            setMessage("Senha atualizada e evento registrado na auditoria.");
          }}
        />
      )}
    </>
  );
}

const companyRoleLabel = (role: string) =>
  role === "owner" ? "ADM da empresa" : role === "admin" ? "Administrador" : role === "viewer" ? "Consulta" : role;

function UserCompaniesModal({
  user,
  companies,
  onClose,
  onSaved,
}: {
  user: ManagedUser;
  companies: Tenant[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // Papel por empresa; a empresa de origem mantém o nível do usuário e é ignorada na gravação.
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [home, setHome] = useState<UserCompanyRole>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .userCompanies(user.id)
      .then((page) => {
        // A origem é referência fixa: o nível vem do cadastro e não é gravado aqui.
        setHome(page.items.find((item) => item.isHome));
        setSelected(
          Object.fromEntries(
            page.items.filter((item) => !item.isHome).map((item) => [item.tenantId, item.role]),
          ),
        );
      })
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Falha ao carregar empresas."),
      );
  }, [user.id]);
  const toggle = (tenantId: string) =>
    setSelected((current) => {
      if (current[tenantId]) {
        const next = { ...current };
        delete next[tenantId];
        return next;
      }
      return { ...current, [tenantId]: user.role };
    });
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      // A fonte é o que está marcado, não a lista de empresas da tela: se ela estiver
      // desatualizada, enviar só `companies` apagaria a concessão ausente (o backend
      // substitui todas as concessões fora da empresa de origem).
      await api.setUserCompanies(
        user.id,
        Object.entries(selected).map(([tenantId, role]) => ({ tenantId, role })),
      );
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao salvar empresas.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose}>
          ×
        </button>
        <p className="eyebrow">MULTIEMPRESA</p>
        <h2>Empresas de {user.displayName}</h2>
        <p className="muted">
          A empresa de origem mantém o nível do cadastro; as marcadas aqui são
          adicionais, cada uma com o próprio nível. Só o Master sem escopo altera.
        </p>
        {error && <p className="error">{error}</p>}
        {!home && !error && <p className="muted">Carregando a empresa de origem…</p>}
        {!home && error && (
          <p className="muted">Sem a empresa de origem não é seguro salvar; feche e abra novamente.</p>
        )}
        <div className="company-checks">
          {home && (
            <div className="company-check company-home">
              <span>
                {companies.find((company) => company.id === home.tenantId)?.name ?? "Empresa de origem"}
                <small>Empresa de origem · nível do cadastro</small>
              </span>
              <strong>{companyRoleLabel(home.role)}</strong>
            </div>
          )}
          {companies
            .filter((company) => company.id !== home?.tenantId)
            .map((company) => (
            <div className="company-check" key={company.id}>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(selected[company.id])}
                  onChange={() => toggle(company.id)}
                />
                {company.name}
              </label>
              {selected[company.id] && (
                <select
                  value={selected[company.id]}
                  onChange={(e) =>
                    setSelected((current) => ({
                      ...current,
                      [company.id]: e.target.value,
                    }))
                  }
                >
                  <option value="owner">ADM da empresa</option>
                  <option value="admin">Administrador</option>
                  <option value="viewer">Consulta</option>
                </select>
              )}
            </div>
          ))}
        </div>
        <div className="row-actions">
          <button className="small-button" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button onClick={() => void save()} disabled={busy || !home}>
            {busy ? "Salvando…" : "Salvar empresas"}
          </button>
        </div>
      </section>
    </div>
  );
}

function AddUserModal({
  partners,
  companies,
  canAssignOwner,
  onClose,
  onCreated,
}: {
  partners: Partner[];
  companies: Tenant[];
  canAssignOwner: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<ManagedUser["role"]>("viewer");
  const [partnerId, setPartnerId] = useState("");
  const [password, setPassword] = useState("");
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const partnerRole = role === "partner_admin" || role === "partner_support";
  // A empresa de origem já vem do cadastro; aqui só as adicionais, com o mesmo papel.
  const canGrantCompanies = !partnerRole && companies.length > 0;
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const created = await api.createUser({
        email,
        displayName,
        role,
        password,
        partnerId: partnerRole ? partnerId : undefined,
      });
      if (canGrantCompanies && companyIds.length > 0)
        await api.setUserCompanies(created.id, companyIds.map((tenantId) => ({ tenantId, role })));
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao criar usuário.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose}>
          ×
        </button>
        <p className="eyebrow">NOVO ACESSO</p>
        <h2>Adicionar usuário</h2>
        <form onSubmit={submit}>
          <label>
            Nome
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              minLength={2}
              maxLength={160}
              required
            />
          </label>
          <label>
            E-mail
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label>
            Nível de acesso
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as ManagedUser["role"])}
            >
              <option value="viewer">User</option>
              <option value="admin">Administrador</option>
              {canAssignOwner && <option value="owner">ADM da empresa</option>}
              {partners.length > 0 && <option value="partner_support">Revenda — Suporte</option>}
              {partners.length > 0 && <option value="partner_admin">Revenda — ADM</option>}
            </select>
          </label>
          {partnerRole && (
            <label>
              Revenda
              <select
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
                required
              >
                <option value="">Selecione a revenda</option>
                {partners.filter((partner) => partner.isActive).map((partner) => (
                  <option key={partner.id} value={partner.id}>{partner.name}</option>
                ))}
              </select>
            </label>
          )}
          <label>
            Senha inicial
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={12}
              maxLength={256}
              autoComplete="new-password"
              required
            />
          </label>
          {canGrantCompanies && (
            <fieldset className="company-checks">
              <legend>Empresas adicionais</legend>
              <small>Além da empresa atual, com o mesmo nível de acesso.</small>
              {companies.map((company) => (
                <label key={company.id} className="company-check">
                  <input
                    type="checkbox"
                    checked={companyIds.includes(company.id)}
                    onChange={(event) =>
                      setCompanyIds((current) =>
                        event.target.checked
                          ? [...current, company.id]
                          : current.filter((id) => id !== company.id),
                      )
                    }
                  />
                  {company.name}
                </label>
              ))}
            </fieldset>
          )}
          <div className="role-help">
            <b>{roleLabel(role)}</b>
            <span>
                {role === "viewer"
                  ? "Consulta equipamentos e timeline, sem alterações."
                  : role === "partner_support"
                    ? "Atende a carteira da revenda sem ver capturas, atividade ou auditoria."
                    : role === "partner_admin"
                      ? "Administra a carteira da revenda, com acesso ao conteúdo das empresas."
                      : role === "admin"
                        ? "Opera equipamentos e políticas, sem gestão de usuários."
                        : "Controle completo da empresa e dos usuários."}
            </span>
          </div>
          {error && <p className="error">{error}</p>}
          <button disabled={busy}>{busy ? "Criando…" : "Criar usuário"}</button>
        </form>
      </section>
    </div>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onSaved,
}: {
  user: ManagedUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirmation) {
      setError("As senhas não coincidem.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.resetUserPassword(user.id, password);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao alterar senha.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose}>
          ×
        </button>
        <p className="eyebrow">CREDENCIAL</p>
        <h2>Nova senha para {user.displayName}</h2>
        <form onSubmit={submit}>
          <label>
            Nova senha
            <input
              type="password"
              minLength={12}
              maxLength={256}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          <label>
            Confirmar senha
            <input
              type="password"
              minLength={12}
              maxLength={256}
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button disabled={busy}>
            {busy ? "Salvando…" : "Atualizar senha"}
          </button>
        </form>
      </section>
    </div>
  );
}

function InventoryPanel({ deviceId }: { deviceId: string }) {
  const [inventory, setInventory] = useState<DeviceInventory | null>(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  useEffect(() => {
    let active = true;
    api
      .inventory(deviceId)
      .then((value) => {
        if (!active) return;
        setInventory(value);
        setError("");
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : "Falha ao carregar o inventário.");
      });
    return () => {
      active = false;
    };
  }, [deviceId]);

  if (error) {
    return (
      <section className="panel">
        <p className="error">{error}</p>
      </section>
    );
  }
  if (!inventory) {
    return (
      <section className="panel">
        <div className="panel-title">
          <h2>Inventário</h2>
        </div>
        <div className="empty">
          <b>Aguardando a primeira coleta</b>
          <span>O agente envia o inventário ao iniciar o serviço.</span>
        </div>
      </section>
    );
  }

  const data = inventory.inventory;
  const hardware = data.hardware;
  const os = data.os;
  const needle = filter.trim().toLowerCase();
  const software = needle
    ? data.software.filter((item) => `${item.name} ${item.publisher ?? ""} ${item.version ?? ""}`.toLowerCase().includes(needle))
    : data.software;
  const services = needle
    ? data.services.filter((item) => `${item.name} ${item.displayName}`.toLowerCase().includes(needle))
    : data.services;
  return (
    <section className="panel">
      <div className="panel-title">
        <h2>Inventário</h2>
        <span>coletado em {new Date(inventory.collectedAt).toLocaleString("pt-BR")}</span>
      </div>
      <div className="inventory-summary">
        <div>
          <small>Fabricante / modelo</small>
          <strong>{[hardware.manufacturer, hardware.model].filter(Boolean).join(" ") || "—"}</strong>
        </div>
        <div>
          <small>Sistema</small>
          <strong>{[os.productName, os.displayVersion, os.build].filter(Boolean).join(" ") || "—"}</strong>
        </div>
        <div>
          <small>CPU</small>
          <strong>{hardware.cpu ?? "—"}{hardware.cpuLogical ? ` · ${hardware.cpuLogical} threads` : ""}</strong>
        </div>
        <div>
          <small>Memória</small>
          <strong>{bytes(hardware.ramBytes)}</strong>
        </div>
        <div>
          <small>BIOS / placa</small>
          <strong>
            {[hardware.biosVendor, hardware.biosVersion].filter(Boolean).join(" ") || "—"}
            {hardware.boardProduct ? ` · ${hardware.boardProduct}` : ""}
          </strong>
        </div>
        <div>
          <small>Último boot (aprox.)</small>
          <strong>{os.lastBootAt ? new Date(os.lastBootAt).toLocaleString("pt-BR") : "—"}</strong>
        </div>
      </div>
      <label className="inventory-filter">
        Filtrar programas e serviços
        <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="ex.: Office, Spooler" />
      </label>
      <details className="inventory-section" open>
        <summary>Programas instalados <b>{data.software.length}</b></summary>
        <div className="inventory-list">
          {software.slice(0, 300).map((item) => (
            <div className="inventory-row" key={`${item.name}|${item.version ?? ""}`}>
              <span>{item.name}</span>
              <span>{[item.version, item.publisher].filter(Boolean).join(" · ")}</span>
            </div>
          ))}
          {!software.length && <div className="muted">Nenhum programa encontrado.</div>}
        </div>
      </details>
      <details className="inventory-section">
        <summary>Serviços <b>{data.services.length}</b></summary>
        <div className="inventory-list">
          {services.slice(0, 300).map((item) => (
            <div className="inventory-row" key={item.name}>
              <span>{item.displayName}<small>{item.name}</small></span>
              <span>{item.status} · {item.startMode}</span>
            </div>
          ))}
          {!services.length && <div className="muted">Nenhum serviço encontrado.</div>}
        </div>
      </details>
      <details className="inventory-section">
        <summary>Discos <b>{data.disks.length}</b></summary>
        <div className="inventory-list">
          {data.disks.map((disk) => (
            <div className="inventory-row" key={disk.name}>
              <span>{disk.name} <small>{disk.fileSystem}</small></span>
              <span>{bytes(disk.freeBytes)} livres de {bytes(disk.totalBytes)}</span>
            </div>
          ))}
          {!data.disks.length && <div className="muted">Nenhum disco encontrado.</div>}
        </div>
      </details>
      <details className="inventory-section">
        <summary>Vídeo e rede <b>{data.gpus.length + data.networks.length}</b></summary>
        <div className="inventory-list">
          {data.gpus.map((gpu) => (
            <div className="inventory-row" key={`gpu-${gpu.name}`}>
              <span>{gpu.name}</span>
              <span>{gpu.driverVersion ? `driver ${gpu.driverVersion}` : ""}</span>
            </div>
          ))}
          {data.networks.map((nic) => (
            <div className="inventory-row" key={`nic-${nic.name}`}>
              <span>{nic.description ?? nic.name}<small>{nic.name}</small></span>
              <span>{nic.macAddress ?? ""}{nic.speedBitsPerSecond ? ` · ${Math.round(nic.speedBitsPerSecond / 1_000_000)} Mbps` : ""}</span>
            </div>
          ))}
        </div>
      </details>
      <details className="inventory-section">
        <summary>Atualizações instaladas <b>{data.hotfixes.length}</b></summary>
        <div className="inventory-chips">
          {data.hotfixes.map((hotfix) => <span key={hotfix}>{hotfix}</span>)}
          {!data.hotfixes.length && <div className="muted">Nenhuma atualização encontrada.</div>}
        </div>
      </details>
    </section>
  );
}

function EventImportPanel({ deviceId, canImport }: { deviceId: string; canImport: boolean }) {
  const [imports, setImports] = useState<EventImport[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const load = () => api.eventImports(deviceId).then(setImports).catch(() => undefined);
  useEffect(() => {
    void load();
  }, [deviceId]);
  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await api.uploadEventImport(deviceId, file);
      setMessage(`"${file.name}" enviado. O agente processa na próxima verificação (até 1 min, se online).`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao importar o arquivo.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel">
      <div className="panel-title">
        <h2>Importar log (.evtx)</h2>
        <span>processado pelo agente</span>
      </div>
      <p className="muted">
        Envie um arquivo .evtx exportado do Visualizador de Eventos. O agente lê o arquivo no
        Windows e envia os eventos para este equipamento.
      </p>
      {canImport && (
        <label className="import-upload">
          <input type="file" accept=".evtx" disabled={busy} onChange={upload} />
          <span>{busy ? "Enviando…" : "Selecionar .evtx"}</span>
        </label>
      )}
      {message && <p className="muted">{message}</p>}
      {error && <p className="error">{error}</p>}
      {imports.length > 0 && (
        <div className="inventory-list">
          {imports.map((item) => (
            <div className="inventory-row" key={item.id}>
              <span>
                {item.fileName}
                <small>
                  {new Date(item.requestedAt).toLocaleString("pt-BR")} · {bytes(item.contentLength)}
                </small>
              </span>
              <span>{importStatus(item)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function importStatus(item: EventImport) {
  switch (item.status) {
    case "pending": return "aguardando agente";
    case "processing": return "processando…";
    case "completed": return `${item.importedCount ?? 0} eventos${item.failedCount ? ` · ${item.failedCount} falhas` : ""}`;
    default: return `falhou${item.error ? `: ${item.error}` : ""}`;
  }
}

function ReportsPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [report, setReport] = useState<EventReportSummary | null>(null);
  const [deviceId, setDeviceId] = useState("");
  const [from, setFrom] = useState(localInput(new Date(Date.now() - 7 * 86400000)));
  const [to, setTo] = useState(localInput(new Date()));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const params = () => ({
    deviceId: deviceId || undefined,
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
  });
  const load = async (event?: FormEvent) => {
    event?.preventDefault();
    setBusy(true);
    setError("");
    try {
      setReport(await api.eventReport(params()));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar o relatório.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    api.devices().then(setDevices).catch(() => undefined);
    void load();
  }, []);
  const exportCsv = async () => {
    setBusy(true);
    setError("");
    try {
      const blob = await api.exportEventsCsv(params());
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `eventos-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      // Revogar no próximo tick evita interromper o download em navegadores que
      // ainda estão consumindo o blob (Safari/Firefox).
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao exportar os eventos.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <header>
        <div>
          <p className="eyebrow">ANÁLISE</p>
          <h1>Relatórios</h1>
          <p className="muted">Resumo dos eventos do Windows por período e equipamento.</p>
        </div>
      </header>
      <form className="panel activity-search" onSubmit={load}>
        <label>Equipamento<select value={deviceId} onChange={(event) => setDeviceId(event.target.value)}><option value="">Todos</option>{devices.map((device) => <option key={device.id} value={device.id}>{device.machineName}</option>)}</select></label>
        <label>De<input type="datetime-local" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
        <label>Até<input type="datetime-local" value={to} onChange={(event) => setTo(event.target.value)} /></label>
        <button disabled={busy}>{busy ? "Carregando…" : "Atualizar"}</button>
        <button type="button" className="secondary" disabled={busy} onClick={exportCsv}>Exportar CSV</button>
      </form>
      {error && <p className="error">{error}</p>}
      {report && (
        <>
          <section className="stats">
            <Metric label="Eventos" value={report.total.toString()} />
            <Metric label="Equipamentos" value={report.topDevices.length.toString()} />
            <Metric label="Período" value={`${new Date(report.from).toLocaleDateString("pt-BR")} – ${new Date(report.to).toLocaleDateString("pt-BR")}`} />
          </section>
          <section className="grid">
            <ReportTable title="Por nível" items={report.byLevel} />
            <ReportTable title="Por categoria" items={report.byCategory} />
            <ReportTable title="Equipamentos com mais eventos" items={report.topDevices.map((item) => ({ key: item.machineName, count: item.count }))} />
            <ReportTable title="Assuntos mais frequentes" items={report.topSubjects} />
          </section>
        </>
      )}
    </>
  );
}

function ReportTable({ title, items }: { title: string; items: Array<{ key: string; count: number }> }) {
  return (
    <article className="panel">
      <div className="panel-title">
        <h2>{title}</h2>
        <span>{items.length}</span>
      </div>
      <div className="inventory-list">
        {items.map((item) => (
          <div className="inventory-row" key={item.key}>
            <span>{item.key}</span>
            <span>{item.count}</span>
          </div>
        ))}
        {!items.length && (
          <div className="empty">
            <b>Sem dados</b>
            <span>Nenhum evento no período.</span>
          </div>
        )}
      </div>
    </article>
  );
}

function EventsPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [events, setEvents] = useState<TenantEvent[]>([]);
  const [deviceId, setDeviceId] = useState("");
  const [level, setLevel] = useState("");
  const [category, setCategory] = useState("");
  const pagedEvents = usePagedList(events);
  const [from, setFrom] = useState(localInput(new Date(Date.now() - 7 * 86400000)));
  const [to, setTo] = useState(localInput(new Date()));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async (event?: FormEvent) => {
    event?.preventDefault();
    setBusy(true);
    setError("");
    try {
      const page = await api.events({
        deviceId: deviceId || undefined,
        level: level || undefined,
        category: category || undefined,
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
        limit: 200,
      });
      setEvents(page.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar eventos.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    api.devices().then(setDevices).catch(() => undefined);
    void load();
  }, []);
  return (
    <>
      <PageHeader
        eyebrow="LOGS DO WINDOWS"
        title="Eventos do Windows"
        subtitle="Eventos de estabilidade e segurança coletados dos equipamentos."
      />
      <FilterBar onSubmit={load} actions={<button disabled={busy}>{busy ? "Carregando…" : "Filtrar"}</button>}>
        <label>Equipamento<select value={deviceId} onChange={(e) => setDeviceId(e.target.value)}><option value="">Todos</option>{devices.map((d) => <option key={d.id} value={d.id}>{d.machineName}</option>)}</select></label>
        <label>Nível<select value={level} onChange={(e) => setLevel(e.target.value)}><option value="">Todos</option><option value="critical">Crítico</option><option value="error">Erro</option><option value="warning">Aviso</option><option value="information">Informação</option></select></label>
        <label>Categoria<select value={category} onChange={(e) => setCategory(e.target.value)}><option value="">Todas</option><option value="crash">Crash</option><option value="bsod">Tela azul</option><option value="power">Energia</option><option value="service">Serviço</option><option value="disk">Disco</option><option value="hardware">Hardware</option><option value="update">Update</option><option value="security">Segurança</option><option value="network">Rede</option><option value="boot">Boot</option><option value="performance">Desempenho</option></select></label>
        <label>De<input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>Até<input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </FilterBar>
      {error && <p className="error">{error}</p>}
      <section className="panel">
        <div className="panel-title">
          <h2>Eventos encontrados</h2>
          <span>{events.length}</span>
        </div>
        <div className="event-list">
          {pagedEvents.pageItems.map((event) => (
            <article key={event.id} className={`event-row level-${event.level}`}>
              <div>
                <strong>{event.title}</strong>
                <small>{event.machineName} · {event.subject ? `${event.subject} · ` : ""}{event.provider} · evento {event.eventId} · {event.channel}{event.source === "import" ? " · importado" : ""}</small>
                {event.message && <details><summary>Detalhes</summary><p>{event.message}</p></details>}
              </div>
              <span className="event-time">{new Date(event.occurredAt).toLocaleString("pt-BR")}</span>
            </article>
          ))}
          {!events.length && (
            <div className="empty">
              <b>Nenhum evento</b>
              <span>Ajuste os filtros ou aguarde a coleta dos equipamentos.</span>
            </div>
          )}
        </div>
        <ListControls page={pagedEvents.page} pages={pagedEvents.pages} size={pagedEvents.size} total={pagedEvents.total} onSize={pagedEvents.changeSize} onPrev={pagedEvents.prev} onNext={pagedEvents.next} />
      </section>
    </>
  );
}

function PoliciesPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  useEffect(() => {
    api.devices().then(setDevices);
  }, []);
  return (
    <>
      <header>
        <div>
          <p className="eyebrow">CONFIGURAÇÃO</p>
          <h1>Políticas</h1>
          <p className="muted">
            Selecione um equipamento para ajustar heartbeat e comportamento das capturas.
          </p>
        </div>
      </header>
      <section className="panel list-cards">
        {devices.map((d) => (
          <a href={`#/devices/${d.id}`} key={d.id}>
            <div>
              <strong>{d.machineName}</strong>
              <small>{d.osVersion}</small>
            </div>
            <span>Configurar →</span>
          </a>
        ))}
        {!devices.length && (
          <div className="empty">
            <b>Nenhum equipamento</b>
            <span>Ative um agente para configurar sua política.</span>
          </div>
        )}
      </section>
    </>
  );
}

function SystemPanels() {
  const [status, setStatus] = useState<SystemStatus>();
  const [network, setNetwork] = useState<NetworkStatus>();
  const [error, setError] = useState("");
  useEffect(() => {
    api
      .systemStatus()
      .then(setStatus)
      .catch((e) => setError(e instanceof Error ? e.message : "Falha ao carregar as métricas."));
    api.networkStatus().then(setNetwork).catch(() => undefined);
  }, []);
  if (error) return <p className="muted">{error}</p>;
  if (!status) return <p className="muted">Carregando métricas do servidor…</p>;
  const server = status.server;
  const memoryUsed = server.memoryTotalBytes - server.memoryAvailableBytes;
  const diskUsed = server.diskTotalBytes - server.diskFreeBytes;
  const percent = (part: number, total: number) => (total > 0 ? `${Math.round((part / total) * 100)}%` : "—");
  const days = Math.floor(server.uptimeHours / 24);
  const hours = Math.round(server.uptimeHours % 24);
  return (
    <section className="settings-grid">
      <article className="panel settings-card">
        <h2>Servidor</h2>
        <dl className="system-metrics">
          <div><dt>Uptime</dt><dd>{days > 0 ? `${days}d ${hours}h` : `${hours}h`}</dd></div>
          <div><dt>Carga (1/5/15 min)</dt><dd>{server.load1.toFixed(2)} · {server.load5.toFixed(2)} · {server.load15.toFixed(2)} <small>({server.cpuCount} vCPU)</small></dd></div>
          <div><dt>Memória</dt><dd>{bytes(memoryUsed)} de {bytes(server.memoryTotalBytes)} ({percent(memoryUsed, server.memoryTotalBytes)})</dd></div>
          <div><dt>Disco</dt><dd>{bytes(diskUsed)} de {bytes(server.diskTotalBytes)} ({percent(diskUsed, server.diskTotalBytes)})</dd></div>
          <div><dt>Postgres</dt><dd>{server.databaseConnections} conexões · {bytes(server.databaseBytes)}</dd></div>
        </dl>
      </article>
      <article className="panel settings-card">
        <h2>Ingestão (última hora)</h2>
        <dl className="system-metrics">
          <div><dt>Equipamentos online</dt><dd>{status.ingestion.devicesOnline}</dd></div>
          <div><dt>Capturas</dt><dd>{status.ingestion.snapshotsLastHour}</dd></div>
          <div><dt>Eventos do Windows</dt><dd>{status.ingestion.eventsLastHour}</dd></div>
          <div><dt>Omissões</dt><dd>{status.ingestion.omissionsLastHour}</dd></div>
        </dl>
      </article>
      <article className="panel settings-card">
        <h2>Storage</h2>
        <dl className="system-metrics">
          <div><dt>Bucket</dt><dd>{status.storage.bucket}</dd></div>
          <div><dt>Endpoint</dt><dd className="break">{status.storage.endpoint}</dd></div>
          <div><dt>Objetos</dt><dd>{status.storage.objects}</dd></div>
          <div><dt>Total</dt><dd>{bytes(status.storage.bytes)}</dd></div>
        </dl>
      </article>

      <article className="panel settings-card">
        <h2>Firewall e IPs banidos</h2>
        {!network ? (
          <p className="muted">Carregando estado da rede…</p>
        ) : (
          <dl className="system-metrics">
            <div><dt>Firewall</dt><dd>{network.firewallActive ? `Ativo · ${network.firewallRules} regras` : "Sem regras ativas"}</dd></div>
            <div><dt>Login por senha (SSH)</dt><dd>{network.passwordAuthentication === "no" ? "Desativado" : network.passwordAuthentication}</dd></div>
            <div><dt>Login de root (SSH)</dt><dd>{network.permitRootLogin}</dd></div>
            <div><dt>Jails (fail2ban)</dt><dd>{network.jails.length ? network.jails.join(", ") : "Não instalado"}</dd></div>
            <div><dt>IPs banidos</dt><dd>{network.bannedIps.length ? network.bannedIps.join(", ") : "Nenhum"}</dd></div>
          </dl>
        )}
        {network && (
          <>
            <h3>Portas em escuta</h3>
            <ul className="muted">
              {network.ports.map((port) => <li key={port} className="break">{port}</li>)}
            </ul>
            <p className="muted">Apenas leitura. Banir/desbanir exige um fluxo auditado próprio.</p>
          </>
        )}
      </article>
    </section>
  );
}

function SettingsPage() {
  const isPlatformAdmin = session.user()?.platformRole === "platform_admin";
  const [settings, setSettings] = useState<PlatformSettings>();
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (isPlatformAdmin) api.platformSettings().then(setSettings).catch((e) => setError(e.message));
  }, [isPlatformAdmin]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!settings) return;
    setBusy(true); setError(""); setMessage("");
    try { await api.updatePlatformSettings(settings); setMessage("Configurações salvas e registradas na auditoria."); setSettings(await api.platformSettings()); }
    catch (e) { setError(e instanceof Error ? e.message : "Falha ao salvar configurações."); }
    finally { setBusy(false); }
  };
  return <>
    <PageHeader eyebrow="PREFERÊNCIAS E OPERAÇÃO" title="Configurações" subtitle="Personalize o painel e os padrões operacionais da plataforma." />
    {!!session.user()?.platformRole && <SystemPanels />}
    {error && <p className="error">{error}</p>}{message && <p className="success notice">{message}</p>}
    <section className="settings-grid">
      <article className="panel settings-card"><h2>Aparência</h2><p className="muted">O tema agora pode ser alternado pelo botão fixo no topo do painel.</p></article>
      {isPlatformAdmin && settings && <article className="panel settings-card"><h2>Operação da plataforma</h2><p className="muted">Limites globais protegidos por auditoria.</p><form onSubmit={save}><label>Retenção da auditoria (dias)<input type="number" min="30" max="3650" value={settings.auditRetentionDays} onChange={(e) => setSettings({ ...settings, auditRetentionDays: Number(e.target.value) })} /></label><label>Intervalo da limpeza (horas)<input type="number" min="1" max="24" value={settings.retentionIntervalHours} onChange={(e) => setSettings({ ...settings, retentionIntervalHours: Number(e.target.value) })} /></label><label>Validade padrão das chaves (horas)<input type="number" min="1" max="720" value={settings.activationKeyDefaultHours} onChange={(e) => setSettings({ ...settings, activationKeyDefaultHours: Number(e.target.value) })} /></label><label>Máximo do acesso de suporte (horas)<input type="number" min="1" max="168" value={settings.supportAccessMaxHours} onChange={(e) => setSettings({ ...settings, supportAccessMaxHours: Number(e.target.value) })} /></label><label>Backup diário do banco<select value={settings.backupEnabled ? "1" : "0"} onChange={(e) => setSettings({ ...settings, backupEnabled: e.target.value === "1" })}><option value="1">Ativado</option><option value="0">Desativado</option></select></label><label>Hora do backup (UTC)<input type="number" min="0" max="23" value={settings.backupHour} onChange={(e) => setSettings({ ...settings, backupHour: Number(e.target.value) })} /></label><label>Retenção do backup (dias)<input type="number" min="1" max="90" value={settings.backupRetentionDays} onChange={(e) => setSettings({ ...settings, backupRetentionDays: Number(e.target.value) })} /></label>{settings.backupLastStatus && <small className="muted">Último backup: {settings.backupLastStatus === "ok" ? "concluído" : settings.backupLastStatus}{settings.backupLastRunAt ? ` em ${new Date(settings.backupLastRunAt).toLocaleString("pt-BR")}` : ""}</small>}<label className="check support-enable"><input type="checkbox" checked={settings.supportWidgetEnabled} onChange={(e) => setSettings({ ...settings, supportWidgetEnabled: e.target.checked })} /> Ativar chat de suporte</label><label className="support-html">HTML do chat de suporte<textarea maxLength={50000} rows={12} value={settings.supportWidgetHtml} onChange={(e) => setSettings({ ...settings, supportWidgetHtml: e.target.value })} /><small>Executado em iframe isolado, sem acesso ao token ou ao painel.</small></label><button disabled={busy}>{busy ? "Salvando…" : "Salvar configurações"}</button></form></article>}
    </section>
  </>;
}

const usageColors = [
  "#e43b40",
  "#f08b33",
  "#e8c547",
  "#42d3aa",
  "#3aa9e8",
  "#7b7bf0",
  "#c65de0",
  "#e05d9a",
  "#8bd450",
  "#6ad0d8",
];

const categoryLabels: Record<string, string> = {
  productive: "Produtivo",
  neutral: "Neutro",
  unproductive: "Improdutivo",
  unclassified: "Sem classificação",
};

// Mesma normalização do servidor: sem extensão e sem diferenciar maiúsculas.
const normalizeProcess = (value: string) => value.trim().replace(/\.exe$/i, "").toLowerCase();

const suggestionKindLabel = (kind: ActivitySuggestion["kind"]) =>
  kind === "device" ? "Equipamento" : kind === "user" ? "Usuário" : "Processo";

function UsageTimelineChart({
  timeline,
  processOrder,
  buckets,
  title,
}: {
  timeline?: ActivityUsageTimeline | null;
  processOrder: string[];
  buckets?: ActivityUsageBucket[];
  title?: string;
}) {
  const columns = buckets ?? timeline?.buckets ?? [];
  if (!timeline || !columns.length) return null;
  const totals = columns.map((bucket) => bucket.slices.reduce((sum, slice) => sum + slice.seconds, 0));
  const maximum = Math.max(1, ...totals);
  const colorFor = (process: string) => {
    if (process === "Outros") return "#5a5a63";
    const index = processOrder.indexOf(process);
    return usageColors[(index >= 0 ? index : 0) % usageColors.length];
  };
  const formatBucket = (start: string) => {
    const date = new Date(start);
    return timeline.bucketMinutes >= 1440
      ? date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
      : date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };
  const step = Math.max(1, Math.ceil(columns.length / 12));
  const label =
    timeline.bucketMinutes >= 1440
      ? "por dia"
      : timeline.bucketMinutes >= 60
        ? `por ${timeline.bucketMinutes / 60}h`
        : `por ${timeline.bucketMinutes} min`;
  const legend = Array.from(new Set(columns.flatMap((bucket) => bucket.slices.map((slice) => slice.process))))
    .sort((a, b) => processOrder.indexOf(a) - processOrder.indexOf(b));
  return (
    <div className="usage-timeline">
      <div className="panel-title">
        <h3>{title ?? "Uso ao longo do tempo"}</h3>
        <span>{label}</span>
      </div>
      <div className="usage-timeline-bars" role="img" aria-label="Tempo por programa ao longo do período">
        {columns.map((bucket, index) => (
          <div className="usage-timeline-col" key={bucket.start}>
            {bucket.slices.map((slice) => (
              <i
                key={slice.process}
                title={`${formatBucket(bucket.start)} · ${slice.process}: ${duration(slice.seconds)}`}
                style={{ height: `${(slice.seconds / maximum) * 100}%`, background: colorFor(slice.process) }}
              />
            ))}
            <span>{index % step === 0 ? formatBucket(bucket.start) : ""}</span>
          </div>
        ))}
      </div>
      <div className="usage-legend">
        {legend.map((process) => (
          <span key={process}>
            <i style={{ background: colorFor(process) }} />
            {process}
          </span>
        ))}
      </div>
    </div>
  );
}

function ActivityPage() {
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState(localInput(new Date(Date.now() - 86400000)));
  const [to, setTo] = useState(localInput(new Date()));
  const [user, setUser] = useState("");
  const [process, setProcess] = useState("");
  const [deviceQuery, setDeviceQuery] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [devices, setDevices] = useState<Device[]>([]);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [report, setReport] = useState<ActivityUsageReport>();
  const [reportFilters, setReportFilters] = useState<{ from: string; to: string; deviceId?: string }>();
  const [suggestions, setSuggestions] = useState<ActivitySuggestion[]>([]);
  const [suggestionIndex, setSuggestionIndex] = useState(-1);
  const [queryOpen, setQueryOpen] = useState(false);
  const [deviceOpen, setDeviceOpen] = useState(false);
  const pagedActivity = usePagedList(events.filter((event) => event.eventType !== "window_list"));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [rules, setRules] = useState<Array<{ pattern: string; kind: string }>>([]);
  const [goalMessage, setGoalMessage] = useState("");
  const [goalBusy, setGoalBusy] = useState(false);
  const canManageGoals = session.user()?.role === "owner" || session.user()?.role === "admin";

  useEffect(() => {
    api.devices().then(setDevices).catch(() => undefined);
  }, []);

  useEffect(() => {
    api
      .activityCategories()
      .then((page) => setRules(page.rules))
      .catch(() => undefined);
  }, []);

  // Sugestões do campo em linguagem natural: pequeno atraso para não consultar o
  // servidor a cada tecla.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .activitySuggestions(term)
        .then((page) => {
          if (cancelled) return;
          setSuggestions(page.items);
          setSuggestionIndex(-1);
        })
        .catch(() => {
          if (!cancelled) setSuggestions([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const normalizedDevice = deviceQuery.trim().toLowerCase();
  const deviceMatches = (normalizedDevice
    ? devices.filter((item) => item.machineName.toLowerCase().includes(normalizedDevice))
    : devices
  ).slice(0, 8);

  const pickSuggestion = (suggestion: ActivitySuggestion) => {
    // A sugestão vira uma frase que o interpretador já entende; assim o operador pode
    // continuar digitando "ontem às 10h" depois de escolher o processo.
    setQuery(
      suggestion.kind === "device"
        ? suggestion.value
        : `${suggestion.kind === "user" ? "usuário" : "processo"} ${suggestion.value}`,
    );
    setSuggestions([]);
    setSuggestionIndex(-1);
    setQueryOpen(false);
  };

  const pickDevice = (device: Device) => {
    setDeviceId(device.id);
    setDeviceQuery(device.machineName);
    setDeviceOpen(false);
  };

  const search = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setQueryOpen(false);
    setDeviceOpen(false);
    try {
      let filters: { from: string; to: string; user?: string; process?: string; deviceId?: string; eventType?: string } = {
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
        user: user || undefined,
        process: process || undefined,
        deviceId: deviceId || undefined,
      };
      if (query.trim()) {
        const parsed = await api.interpretActivity(query);
        filters = {
          from: parsed.from,
          to: parsed.to,
          user: parsed.user || filters.user,
          process: parsed.process || filters.process,
          deviceId: parsed.deviceId ?? filters.deviceId,
          eventType: parsed.eventType,
        };
        setFrom(localInput(new Date(parsed.from)));
        setTo(localInput(new Date(parsed.to)));
        setUser(filters.user ?? "");
        setProcess(filters.process ?? "");
        if (parsed.deviceId) {
          const match = devices.find((item) => item.id === parsed.deviceId);
          setDeviceId(parsed.deviceId);
          setDeviceQuery(match?.machineName ?? "");
        }
      }
      setEvents((await api.searchActivity(filters)).items);
      const applied = { from: filters.from, to: filters.to, deviceId: filters.deviceId };
      setReportFilters(applied);
      setReport(await api.activityReport(applied));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha na pesquisa.");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    if (!reportFilters) return;
    setBusy(true);
    setError("");
    try {
      const blob = await api.exportActivityReport(reportFilters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `produtividade-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      // Revogar no próximo tick evita interromper o download em navegadores que
      // ainda estão consumindo o blob (Safari/Firefox).
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao exportar o relatório.");
    } finally {
      setBusy(false);
    }
  };

  const goalFor = (process: string) =>
    rules.find((rule) => rule.pattern === normalizeProcess(process))?.kind ?? "unclassified";

  const setGoal = (process: string, kind: string) => {
    const pattern = normalizeProcess(process);
    setRules((current) => {
      const rest = current.filter((rule) => rule.pattern !== pattern);
      return kind === "unclassified" ? rest : [...rest, { pattern, kind }];
    });
    setGoalMessage("");
  };

  const saveGoals = async () => {
    setGoalBusy(true);
    setError("");
    setGoalMessage("");
    try {
      const saved = await api.saveActivityCategories(rules);
      setRules(saved.rules);
      setGoalMessage("Metas salvas. Os totais por categoria foram recalculados.");
      if (reportFilters) setReport(await api.activityReport(reportFilters));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao salvar as metas.");
    } finally {
      setGoalBusy(false);
    }
  };

  const periodLabel = reportFilters
    ? `${new Date(reportFilters.from).toLocaleDateString("pt-BR")} – ${new Date(reportFilters.to).toLocaleDateString("pt-BR")}`
    : "";

  return (
    <>
      <PageHeader
        eyebrow="ATIVIDADE DO USUÁRIO"
        title="Atividade do usuário"
        subtitle="Localize quem, quando e onde; abra a evidência temporal correspondente."
      />
      <FilterBar onSubmit={search} actions={<button disabled={busy}>{busy ? "Pesquisando…" : "Pesquisar"}</button>}>
        <label className="natural-search suggestion-field">
          Pesquisa em linguagem natural
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setQueryOpen(true);
            }}
            onFocus={() => setQueryOpen(true)}
            onBlur={() => window.setTimeout(() => setQueryOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setSuggestions([]);
                return;
              }
              if (!suggestions.length) return;
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setSuggestionIndex((index) => (index + 1) % suggestions.length);
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSuggestionIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
              } else if (e.key === "Enter" && suggestionIndex >= 0) {
                e.preventDefault();
                pickSuggestion(suggestions[suggestionIndex]);
              }
            }}
            placeholder="Quem abriu o software X ontem às 10h?"
            autoComplete="off"
          />
          {queryOpen && suggestions.length > 0 && (
            <ul className="suggestions" role="listbox">
              {suggestions.map((item, index) => (
                <li key={`${item.kind}-${item.value}`}>
                  <button
                    type="button"
                    className={index === suggestionIndex ? "active" : ""}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickSuggestion(item);
                    }}
                  >
                    <span>{item.value}</span>
                    <small>{suggestionKindLabel(item.kind)}</small>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </label>
        <label className="suggestion-field">
          Equipamento
          <input
            value={deviceQuery}
            onChange={(e) => {
              setDeviceQuery(e.target.value);
              setDeviceId("");
              setDeviceOpen(true);
            }}
            onFocus={() => setDeviceOpen(true)}
            onBlur={() => window.setTimeout(() => setDeviceOpen(false), 150)}
            placeholder="Todos os equipamentos"
            autoComplete="off"
          />
          {deviceOpen && (
            <ul className="suggestions" role="listbox">
              <li>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setDeviceId("");
                    setDeviceQuery("");
                    setDeviceOpen(false);
                  }}
                >
                  <span>Todos os equipamentos</span>
                  <small>sem filtro</small>
                </button>
              </li>
              {deviceMatches.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickDevice(item);
                    }}
                  >
                    <span>{item.machineName}</span>
                    <small>{item.status === "online" ? "online" : "offline"}</small>
                  </button>
                </li>
              ))}
              {!deviceMatches.length && (
                <li>
                  <button type="button" disabled>
                    <span>Nenhum equipamento encontrado</span>
                  </button>
                </li>
              )}
            </ul>
          )}
        </label>
        <label>
          De
          <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          Até
          <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label>
          Usuário
          <input value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" />
        </label>
        <label>
          Processo
          <input value={process} onChange={(e) => setProcess(e.target.value)} autoComplete="off" />
        </label>
      </FilterBar>
      {error && <p className="error">{error}</p>}
      {report && (
        <section className="panel usage-report">
          <div className="panel-title">
            <h2>Tempo por programa</h2>
            <span>
              {report.sampleCount} amostras{periodLabel ? ` · ${periodLabel}` : ""}
            </span>
          </div>
          <div className="print-only">Relatório de produtividade{periodLabel ? ` · ${periodLabel}` : ""}</div>
          <div className="usage-cards">
            <div>
              <span>Tempo ativo</span>
              <strong>{duration(report.activeSeconds)}</strong>
            </div>
            <div>
              <span>Tempo ocioso</span>
              <strong>{duration(report.idleSeconds)}</strong>
            </div>
            <div className="usage-actions">
              <button type="button" className="small-button" disabled={busy} onClick={exportCsv}>
                Exportar CSV (Excel)
              </button>
              <button type="button" className="small-button" onClick={() => window.print()}>
                Imprimir / PDF
              </button>
            </div>
          </div>
          {report.categories && report.categories.length > 0 && (
            <div className="usage-categories">
              {report.categories.map((item) => (
                <div key={item.kind} className={`usage-category ${item.kind}`}>
                  <span>{categoryLabels[item.kind] ?? item.kind}</span>
                  <strong>{duration(item.seconds)}</strong>
                  <small>{Math.round(item.share * 100)}% do tempo ativo</small>
                </div>
              ))}
            </div>
          )}
          {report.processes.length ? (
            <div className="usage-chart">
              <div className="usage-scale">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
              {report.processes.map((item, index) => (
                <div className="usage-row" key={item.process}>
                  <span title={item.process}>{item.process}</span>
                  <div className="usage-track">
                    <i
                      style={{
                        width: `${Math.max(1.5, item.share * 100)}%`,
                        background: usageColors[index % usageColors.length],
                      }}
                    />
                  </div>
                  <b>
                    {duration(item.seconds)} · {Math.round(item.share * 100)}%
                  </b>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted usage-empty">
              Sem atividade suficiente no período. O companion precisa estar em execução para registrar trocas de
              janela.
            </p>
          )}
          <UsageTimelineChart
            timeline={report.timeline}
            processOrder={report.processes.map((item) => item.process)}
          />
          {report.timeline?.byDevice && report.timeline.byDevice.length > 1 && (
            <details className="usage-devices">
              <summary>Por equipamento ({report.timeline.byDevice.length})</summary>
              {report.timeline.byDevice.map((device) => (
                <UsageTimelineChart
                  key={device.deviceId}
                  timeline={report.timeline}
                  processOrder={report.processes.map((item) => item.process)}
                  buckets={device.buckets}
                  title={device.machineName}
                />
              ))}
            </details>
          )}
          {canManageGoals && report.processes.length > 0 && (
            <div className="usage-goals">
              <div className="panel-title">
                <h3>Metas de produtividade</h3>
                <span>classifique os programas mais usados</span>
              </div>
              {report.processes.map((item) => (
                <label key={item.process}>
                  <span title={item.process}>{item.process}</span>
                  <select
                    value={goalFor(item.process)}
                    onChange={(event) => setGoal(item.process, event.target.value)}
                  >
                    <option value="unclassified">Sem classificação</option>
                    <option value="productive">Produtivo</option>
                    <option value="neutral">Neutro</option>
                    <option value="unproductive">Improdutivo</option>
                  </select>
                </label>
              ))}
              <div className="usage-goals-actions">
                <button type="button" className="small-button" disabled={goalBusy} onClick={saveGoals}>
                  {goalBusy ? "Salvando…" : "Salvar metas"}
                </button>
                {goalMessage && <small>{goalMessage}</small>}
              </div>
            </div>
          )}
        </section>
      )}
      <section className="panel">
        <div className="panel-title">
          <h2>Eventos encontrados</h2>
          <span>{pagedActivity.total} resultados</span>
        </div>
        <div className="activity-results">
          {pagedActivity.pageItems.map((item) => (
            <article key={item.id}>
              <div>
                <strong>
                  {item.machineName} · {item.accountName}
                </strong>
                <span>{item.eventType.replaceAll("_", " ")}</span>
              </div>
              <div>
                <b>{item.processName ?? "—"}</b>
                <small>{item.windowTitle ?? new Date(item.agentTimestamp).toLocaleString("pt-BR")}</small>
              </div>
              {item.nearestSnapshotId ? (
                <a href={`#/devices/${item.deviceId}/timeline?at=${encodeURIComponent(item.agentTimestamp)}`}>
                  Abrir evidência →
                </a>
              ) : (
                <small>Sem captura próxima</small>
              )}
            </article>
          ))}
          {!events.length && (
            <div className="empty">
              <b>Nenhum evento carregado</b>
              <span>Use a pesquisa acima.</span>
            </div>
          )}
        </div>
        <ListControls
          page={pagedActivity.page}
          pages={pagedActivity.pages}
          size={pagedActivity.size}
          total={pagedActivity.total}
          onSize={pagedActivity.changeSize}
          onPrev={pagedActivity.prev}
          onNext={pagedActivity.next}
        />
      </section>
    </>
  );
}

function CompaniesPage() {
  const currentUser = session.user();
  const scoped = isScopedSession(session.token(), currentUser);
  const isAdmin = currentUser?.platformRole === "platform_admin" && !scoped;
  // Revenda enxerga a carteira, mas não cria empresa nem plano; o Suporte Guardian Insight
  // pode criar empresa (tenant.manage) embora não veja conteúdo.
  const canManageTenants = can(currentUser, "tenant.manage") && !scoped;
  const isPortfolio = can(currentUser, "portfolio.read") && !canManageTenants;
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [platformUsers, setPlatformUsers] = useState<PlatformUser[]>([]);
  const [supportUsers, setSupportUsers] = useState<ManagedUser[]>([]);
  const [grants, setGrants] = useState<SupportGrant[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant>();
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const pagedTenants = usePagedList(tenants);
  const refresh = async () => {
    setError("");
    try {
      setTenants(isPortfolio ? await api.myPortfolio() : await api.tenants());
      if (isAdmin) {
        const [users, support, activeGrants, partnerList] = await Promise.all([
          api.platformUsers(),
          api.supportUsers(),
          api.supportGrants(),
          api.partners(),
        ]);
        setPlatformUsers(users);
        setSupportUsers(support);
        setGrants(activeGrants);
        setPartners(partnerList);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar empresas.");
    }
  };
  useEffect(() => {
    void refresh();
  }, []);
  const enter = async (tenant: Tenant) => {
    try {
      const result = await api.enterTenant(tenant.id);
      session.save(result.accessToken, result.user);
      location.hash = "#/";
      location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Acesso não autorizado.");
    }
  };
  const assignPartner = async (tenant: Tenant, partnerId: string) => {
    setError("");
    try {
      await api.assignPartner(tenant.id, partnerId || undefined);
      setMessage("Revenda da empresa atualizada e auditada.");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao vincular revenda.");
    }
  };
  const updateRole = async (
    user: PlatformUser,
    role?: PlatformUser["platformRole"],
  ) => {
    try {
      await api.updatePlatformRole(user.id, role);
      setMessage("Papel global atualizado e auditado.");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao atualizar papel.");
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="OPERAÇÃO MULTIEMPRESA"
        title={isPortfolio ? "Carteira de empresas" : "Empresas"}
        subtitle={
          isPortfolio
            ? "Empresas atendidas pela sua revenda. Cada acesso abre um contexto isolado e é auditado."
            : "Cada acesso abre um contexto isolado e gera registro de auditoria."
        }
        actions={canManageTenants && <button onClick={() => setShowCreate(true)}>+ Empresa</button>}
      />
      {error && <p className="error">{error}</p>}
      {message && <p className="success notice">{message}</p>}
      <section className="company-grid">
        {pagedTenants.pageItems.map((tenant) => (
          <article className="panel company-card" key={tenant.id}>
            <div>
              <small>{tenant.slug}</small>
              <h2>{tenant.name}</h2>
              {tenant.partnerName && <span className="tag-chip">{tenant.partnerName}</span>}
            </div>
            <dl>
              <div><dt>Usuários</dt><dd>{tenant.users}</dd></div>
              <div><dt>Equipamentos</dt><dd>{tenant.devices}</dd></div>
              {tenant.allocations.map((allocation) => <div key={allocation.planId}><dt>{allocation.planName}</dt><dd>{allocation.usedDevices}/{allocation.licensedDevices} vagas</dd></div>)}
            </dl>
            {isAdmin && (
              <label className="partner-assign">
                Revenda
                <select
                  value={tenant.partnerId ?? ""}
                  onChange={(event) => assignPartner(tenant, event.target.value)}
                >
                  <option value="">Sem revenda</option>
                  {partners.filter((partner) => partner.isActive).map((partner) => (
                    <option key={partner.id} value={partner.id}>{partner.name}</option>
                  ))}
                </select>
              </label>
            )}
            <button
              className={tenant.id === currentUser?.tenantId ? "secondary" : ""}
              onClick={() => enter(tenant)}
            >
              {tenant.id === currentUser?.tenantId ? "Atualizar contexto" : "Acessar empresa"}
            </button>
            {canManageTenants && <button className="secondary" onClick={() => setEditingTenant(tenant)}>Editar dados</button>}
          </article>
        ))}
        <ListControls page={pagedTenants.page} pages={pagedTenants.pages} size={pagedTenants.size} total={pagedTenants.total} onSize={pagedTenants.changeSize} onPrev={pagedTenants.prev} onNext={pagedTenants.next} />
      </section>
      {isAdmin && <PartnersPanel partners={partners} onChanged={refresh} />}
      {isAdmin && (
        <section className="panel platform-users">
          <div className="panel-title"><h2>Equipe interna Guardian Insight</h2><span>Papéis globais</span></div>
          <div className="table-wrap">
            <table><thead><tr><th>Usuário</th><th>Empresa base</th><th>Papel global</th><th>Ação</th></tr></thead>
              <tbody>{platformUsers.map((user) => (
                <tr key={user.id}><td><strong>{user.displayName}</strong><small>{user.email}</small></td><td>{user.tenantName}</td><td>{user.platformRole ?? "Sem acesso global"}</td><td><select value={user.platformRole ?? ""} onChange={(event) => updateRole(user, (event.target.value || undefined) as PlatformUser["platformRole"] | undefined)}><option value="">Sem acesso global</option><option value="platform_support">Suporte da plataforma</option><option value="platform_admin">Administrador da plataforma</option></select></td></tr>
              ))}</tbody></table>
          </div>
        </section>
      )}
      {isAdmin && supportUsers.length > 0 && (
        <SupportAccessPanel tenants={tenants} users={supportUsers} grants={grants} onChanged={refresh} />
      )}
      {showCreate && <CreateTenantModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); void refresh(); }} />}
      {editingTenant && <EditTenantModal tenant={editingTenant} onClose={() => setEditingTenant(undefined)} onSaved={() => { setEditingTenant(undefined); setMessage("Dados da empresa atualizados e auditados."); void refresh(); }} />}
    </>
  );
}

function PartnersPanel({ partners, onChanged }: { partners: Partner[]; onChanged: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.createPartner(name, slug);
      setName(""); setSlug("");
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao criar revenda.");
    } finally {
      setBusy(false);
    }
  };
  const toggle = async (partner: Partner) => {
    setError("");
    try {
      await api.updatePartner(partner.id, partner.name, !partner.isActive);
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao atualizar revenda.");
    }
  };
  return (
    <section className="panel partners-panel">
      <div className="panel-title"><h2>Revendas</h2><span>{partners.length} cadastradas</span></div>
      <form className="partner-form" onSubmit={create}>
        <label>Nome<input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required /></label>
        <label>Identificador<input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} pattern="[a-z0-9-]{2,40}" maxLength={40} required /></label>
        <button disabled={busy}>{busy ? "Criando…" : "Criar revenda"}</button>
      </form>
      {error && <p className="error">{error}</p>}
      <div className="table-wrap">
        <table>
          <thead><tr><th>Revenda</th><th>Empresas</th><th>Usuários</th><th>Estado</th><th>Ação</th></tr></thead>
          <tbody>
            {partners.map((partner) => (
              <tr key={partner.id}>
                <td><strong>{partner.name}</strong><small>{partner.slug}</small></td>
                <td>{partner.companies}</td>
                <td>{partner.users}</td>
                <td>{partner.isActive ? "Ativa" : "Inativa"}</td>
                <td><button className="small-button" onClick={() => toggle(partner)}>{partner.isActive ? "Desativar" : "Ativar"}</button></td>
              </tr>
            ))}
            {!partners.length && <tr><td colSpan={5}><span className="muted">Nenhuma revenda cadastrada.</span></td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [error, setError] = useState("");
  const refresh = async () => {
    setError("");
    try {
      const [catalog, companies] = await Promise.all([api.platformPlans(), api.tenants()]);
      setPlans(catalog);
      setTenants(companies);
    } catch (e) { setError(e instanceof Error ? e.message : "Falha ao carregar planos e contratos."); }
  };
  useEffect(() => { void refresh(); }, []);
  return <>
    <PageHeader eyebrow="GESTÃO COMERCIAL" title="Planos e contratos" subtitle="Defina retenções e distribua vagas por empresa antes de ativar os equipamentos." />
    {error && <p className="error">{error}</p>}
    <PlansPanel plans={plans} tenants={tenants} onChanged={refresh} />
  </>;
}

function PlansPanel({ plans, tenants, onChanged }: { plans: Plan[]; tenants: Tenant[]; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState<Plan>();
  const [newPlan, setNewPlan] = useState(false);
  const [error, setError] = useState("");
  const savePlan = async (plan: Plan) => {
    setError("");
    try {
      if (newPlan) await api.createPlan({ code: plan.code, name: plan.name, screenRetentionDays: plan.screenRetentionDays, operationalRetentionDays: plan.operationalRetentionDays, sortOrder: plan.sortOrder, isActive: plan.isActive, erpPrimaryId: plan.erpPrimaryId, erpSecondaryId: plan.erpSecondaryId });
      else await api.updatePlan(plan);
      setEditing(undefined); setNewPlan(false); await onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : "Falha ao salvar plano."); }
  };
  const updateSeat = async (tenantId: string, planId: string, current: number) => {
    const raw = prompt("Quantidade de equipamentos contratada:", String(current));
    if (raw == null) return;
    const quantity = Number(raw);
    if (!Number.isInteger(quantity) || quantity < 0) return setError("Informe uma quantidade inteira válida.");
    try { await api.updateAllocation(tenantId, planId, quantity); await onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : "Falha ao atualizar vagas."); }
  };
  return <section className="panel plans-panel">
    <div className="panel-title"><div><h2>Planos e contratos</h2><span>Auditoria do sistema: 365 dias em todos os planos</span></div><button onClick={() => { setNewPlan(true); setEditing({ id: "", code: "", name: "", screenRetentionDays: 7, operationalRetentionDays: 15, sortOrder: (plans.length + 1) * 10, isActive: true }); }}>+ Plano</button></div>
    {error && <p className="error">{error}</p>}
    <div className="table-wrap"><table><thead><tr><th>Plano</th><th>Telas</th><th>Telemetria/eventos</th><th>Estado</th><th>Ação</th></tr></thead><tbody>{plans.map((plan) => <tr key={plan.id}><td><strong>{plan.name}</strong><small>{plan.code}</small></td><td>{plan.screenRetentionDays} dias</td><td>{plan.operationalRetentionDays} dias</td><td>{plan.isActive ? "Ativo" : "Inativo"}</td><td><button className="small-button" onClick={() => { setNewPlan(false); setEditing(plan); }}>Editar</button></td></tr>)}</tbody></table></div>
    <div className="allocation-grid">{tenants.map((tenant) => <article key={tenant.id}><strong>{tenant.name}</strong>{plans.filter((p) => p.isActive).map((plan) => { const allocation = tenant.allocations.find((a) => a.planId === plan.id); return <button className="secondary" key={plan.id} onClick={() => updateSeat(tenant.id, plan.id, allocation?.licensedDevices ?? 0)}>{plan.name}: {allocation?.usedDevices ?? 0}/{allocation?.licensedDevices ?? 0}</button>; })}</article>)}</div>
    {editing && <PlanModal plan={editing} onClose={() => { setEditing(undefined); setNewPlan(false); }} onSave={savePlan} />}
  </section>;
}

function PlanModal({ plan, onClose, onSave }: { plan: Plan; onClose: () => void; onSave: (plan: Plan) => Promise<void> }) {
  const [draft, setDraft] = useState(plan);
  return <div className="modal-backdrop" onMouseDown={onClose}><section className="modal" onMouseDown={(e) => e.stopPropagation()}><button className="close" onClick={onClose}>×</button><p className="eyebrow">CATÁLOGO COMERCIAL</p><h2>{plan.id ? "Editar plano" : "Novo plano"}</h2><form onSubmit={(e) => { e.preventDefault(); void onSave(draft); }}><label>Nome<input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required /></label><label>Código interno<input pattern="[a-z0-9][a-z0-9-]*[a-z0-9]" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toLowerCase() })} required /></label><label>Retenção de telas (dias)<input type="number" min="1" max="3650" value={draft.screenRetentionDays} onChange={(e) => setDraft({ ...draft, screenRetentionDays: Number(e.target.value) })} required /></label><label>Retenção de telemetria e eventos (dias)<input type="number" min="1" max="3650" value={draft.operationalRetentionDays} onChange={(e) => setDraft({ ...draft, operationalRetentionDays: Number(e.target.value) })} required /></label><label>ID ERP principal<input maxLength={80} value={draft.erpPrimaryId ?? ""} onChange={(e) => setDraft({ ...draft, erpPrimaryId: e.target.value })} placeholder="Conta Azul / Hermes" /></label><label>ID ERP secundário<input maxLength={80} value={draft.erpSecondaryId ?? ""} onChange={(e) => setDraft({ ...draft, erpSecondaryId: e.target.value })} /></label><label className="check"><input type="checkbox" checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} /> Plano ativo</label><button>Salvar plano</button></form></section></div>;
}

type CommercialFields = { erpPrimaryId: string; erpSecondaryId: string; crmPrimaryId: string; crmSecondaryId: string; emailPrimary: string; emailSupport: string; emailBilling: string; phone: string; whatsappPrimary: string; whatsappSupport: string; cpf: string; cnpj: string };
const emptyCommercial: CommercialFields = { erpPrimaryId: "", erpSecondaryId: "", crmPrimaryId: "", crmSecondaryId: "", emailPrimary: "", emailSupport: "", emailBilling: "", phone: "", whatsappPrimary: "", whatsappSupport: "", cpf: "", cnpj: "" };

/** Campos comerciais compartilhados entre criar e editar empresa (ERP/CRM e contato). */
function TenantCommercialFields({ value, onChange }: { value: CommercialFields; onChange: (next: CommercialFields) => void }) {
  const set = (field: keyof CommercialFields) => (event: ChangeEvent<HTMLInputElement>) => onChange({ ...value, [field]: event.target.value });
  return (
    <details className="commercial-fields">
      <summary>Dados comerciais e contato</summary>
      <p className="muted">Usados na integração com ERP/CRM e no contato de cobrança. CPF ou CNPJ é obrigatório.</p>
      <div className="commercial-grid">
        <label>ID ERP principal<input maxLength={80} value={value.erpPrimaryId} onChange={set("erpPrimaryId")} /></label>
        <label>ID ERP secundário<input maxLength={80} value={value.erpSecondaryId} onChange={set("erpSecondaryId")} /></label>
        <label>ID CRM principal<input maxLength={80} value={value.crmPrimaryId} onChange={set("crmPrimaryId")} /></label>
        <label>ID CRM secundário<input maxLength={80} value={value.crmSecondaryId} onChange={set("crmSecondaryId")} /></label>
        <label>CPF<input inputMode="numeric" maxLength={14} placeholder="000.000.000-00" value={value.cpf} onChange={set("cpf")} /></label>
        <label>CNPJ<input inputMode="numeric" maxLength={18} placeholder="00.000.000/0000-00" value={value.cnpj} onChange={set("cnpj")} /></label>
        <label>E-mail principal<input type="email" value={value.emailPrimary} onChange={set("emailPrimary")} /></label>
        <label>E-mail de suporte<input type="email" value={value.emailSupport} onChange={set("emailSupport")} /></label>
        <label>E-mail de faturamento<input type="email" value={value.emailBilling} onChange={set("emailBilling")} /></label>
        <label>Telefone<input inputMode="tel" placeholder="+55 11 99999-9999" value={value.phone} onChange={set("phone")} /></label>
        <label>WhatsApp principal<input inputMode="tel" value={value.whatsappPrimary} onChange={set("whatsappPrimary")} /></label>
        <label>WhatsApp de suporte<input inputMode="tel" value={value.whatsappSupport} onChange={set("whatsappSupport")} /></label>
      </div>
    </details>
  );
}

function EditTenantModal({ tenant, onClose, onSaved }: { tenant: Tenant; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(tenant.name);
  const [commercial, setCommercial] = useState<CommercialFields>({
    erpPrimaryId: tenant.erpPrimaryId ?? "", erpSecondaryId: tenant.erpSecondaryId ?? "",
    crmPrimaryId: tenant.crmPrimaryId ?? "", crmSecondaryId: tenant.crmSecondaryId ?? "",
    emailPrimary: tenant.emailPrimary ?? "", emailSupport: tenant.emailSupport ?? "", emailBilling: tenant.emailBilling ?? "",
    phone: tenant.phone ?? "", whatsappPrimary: tenant.whatsappPrimary ?? "", whatsappSupport: tenant.whatsappSupport ?? "",
    cpf: tenant.cpf ?? "", cnpj: tenant.cnpj ?? "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.updateTenant(tenant.id, { name, ...commercial });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao salvar os dados da empresa.");
    } finally {
      setBusy(false);
    }
  };
  return <div className="modal-backdrop" onMouseDown={onClose}><section className="modal" onMouseDown={(event) => event.stopPropagation()}><button className="close" onClick={onClose}>×</button><p className="eyebrow">DADOS DA EMPRESA</p><h2>{tenant.name}</h2><form onSubmit={submit}><label>Razão ou nome da empresa<input value={name} onChange={(event) => setName(event.target.value)} required /></label><TenantCommercialFields value={commercial} onChange={setCommercial} />{error && <p className="error">{error}</p>}<button disabled={busy}>{busy ? "Salvando…" : "Salvar dados"}</button></form></section></div>;
}

function CreateTenantModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [commercial, setCommercial] = useState<CommercialFields>(emptyCommercial);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try { await api.createTenant({ name, slug, ownerEmail, ownerName, ownerPassword, ...commercial }); onCreated(); }
    catch (e) { setError(e instanceof Error ? e.message : "Falha ao criar empresa."); }
  };
  return <div className="modal-backdrop" onMouseDown={onClose}><section className="modal" onMouseDown={(event) => event.stopPropagation()}><button className="close" onClick={onClose}>×</button><p className="eyebrow">NOVA EMPRESA</p><h2>Criar ambiente isolado</h2><form onSubmit={submit}><label>Razão ou nome da empresa<input value={name} onChange={(event) => { setName(event.target.value); setSlug(event.target.value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")); }} required /></label><label>Identificador<input pattern="[a-z0-9][a-z0-9-]{1,78}[a-z0-9]" value={slug} onChange={(event) => setSlug(event.target.value.toLowerCase())} required /></label><label>Nome do proprietário<input value={ownerName} onChange={(event) => setOwnerName(event.target.value)} required /></label><label>E-mail do proprietário<input type="email" value={ownerEmail} onChange={(event) => setOwnerEmail(event.target.value)} required /></label><label>Senha inicial<input type="password" minLength={12} value={ownerPassword} onChange={(event) => setOwnerPassword(event.target.value)} autoComplete="new-password" required /></label><TenantCommercialFields value={commercial} onChange={setCommercial} />{error && <p className="error">{error}</p>}<button>Criar empresa</button></form></section></div>;
}

function SupportAccessPanel({ tenants, users, grants, onChanged }: { tenants: Tenant[]; users: ManagedUser[]; grants: SupportGrant[]; onChanged: () => Promise<void> }) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id ?? "");
  const [supportUserId, setSupportUserId] = useState(users[0]?.id ?? "");
  const [justification, setJustification] = useState("");
  const create = async (event: FormEvent) => {
    event.preventDefault();
    await api.createSupportGrant(tenantId, supportUserId, justification, new Date(Date.now() + 2 * 3600000).toISOString());
    setJustification("");
    await onChanged();
  };
  return <section className="panel support-access"><div className="panel-title"><h2>Acessos temporários</h2><span>Expiração automática em 2 horas</span></div><form onSubmit={create}><label>Empresa<select value={tenantId} onChange={(event) => setTenantId(event.target.value)}>{tenants.map((tenant) => <option value={tenant.id} key={tenant.id}>{tenant.name}</option>)}</select></label><label>Analista<select value={supportUserId} onChange={(event) => setSupportUserId(event.target.value)}>{users.map((user) => <option value={user.id} key={user.id}>{user.displayName}</option>)}</select></label><label>Justificativa<input minLength={10} maxLength={500} value={justification} onChange={(event) => setJustification(event.target.value)} required /></label><button>Autorizar por 2 horas</button></form><div className="grant-list">{grants.map((grant) => <div key={grant.id}><span><strong>{grant.supportName}</strong>{grant.tenantName} · até {new Date(grant.expiresAt).toLocaleString("pt-BR")}</span><small>{grant.justification}</small>
                  <em className={grant.revokedAt ? "grant-status revoked" : new Date(grant.expiresAt) <= new Date() ? "grant-status expired" : "grant-status active"}>
                    {grant.revokedAt ? "Revogado" : new Date(grant.expiresAt) <= new Date() ? "Expirado" : "Ativo"}
                  </em>{!grant.revokedAt && new Date(grant.expiresAt) > new Date() && <button className="small-button danger" onClick={async () => { await api.revokeSupportGrant(grant.id); await onChanged(); }}>Revogar</button>}</div>)}</div></section>;
}

function AuditPage() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [error, setError] = useState("");
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [target, setTarget] = useState("");
  const [ip, setIp] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const filtered = events.filter((event) => {
    const occurred = new Date(event.occurredAt).getTime();
    if (from && occurred < new Date(from).getTime()) return false;
    if (to && occurred > new Date(to).getTime()) return false;
    if (action && !event.action.toLowerCase().includes(action.toLowerCase())) return false;
    if (actor && !event.actorType.toLowerCase().includes(actor.toLowerCase())) return false;
    if (target && !`${event.targetType ?? ""} ${event.targetId ?? ""}`.toLowerCase().includes(target.toLowerCase())) return false;
    if (ip && !(event.ipAddress ?? "").includes(ip.trim())) return false;
    return true;
  });
  const paged = usePagedList(filtered);
  useEffect(() => {
    api
      .audit()
      .then(setEvents)
      .catch((e) => setError(e.message));
  }, []);
  return (
    <>
      <PageHeader
        eyebrow="SEGURANÇA"
        title="Auditoria"
        subtitle="Registro imutável das ações administrativas e dos agentes."
      />
      {error && <p className="error">{error}</p>}
      <FilterBar>
        <label>
          Ação
          <input value={action} onChange={(event) => setAction(event.target.value)} placeholder="ex.: auth.login" />
        </label>
        <label>
          Ator (tipo)
          <input value={actor} onChange={(event) => setActor(event.target.value)} placeholder="ex.: user" />
        </label>
        <label>
          Alvo (tipo ou id)
          <input value={target} onChange={(event) => setTarget(event.target.value)} placeholder="ex.: device" />
        </label>
        <label>
          IP
          <input value={ip} onChange={(event) => setIp(event.target.value)} placeholder="ex.: 192.168." />
        </label>
        <label>
          De
          <input type="datetime-local" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          Até
          <input type="datetime-local" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
      </FilterBar>
      <section className="panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Quando</th>
                <th>Ação</th>
                <th>Ator</th>
                <th>Alvo</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {paged.pageItems.map((event) => (
                <tr key={event.id}>
                  <td>{new Date(event.occurredAt).toLocaleString("pt-BR")}</td>
                  <td>
                    <strong>{event.action}</strong>
                  </td>
                  <td>{event.actorType}</td>
                  <td>{event.targetType ?? "—"}</td>
                  <td>{event.ipAddress ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filtered.length && (
            <div className="empty">
              <b>Nenhum evento</b>
              <span>Ajuste os filtros ou o período.</span>
            </div>
          )}
        </div>
        <ListControls page={paged.page} pages={paged.pages} size={paged.size} total={paged.total} onSize={paged.changeSize} onPrev={paged.prev} onNext={paged.next} />
      </section>
    </>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: string;
}) {
  return (
    <article className={`metric ${tone ?? ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}
